/**
 * Bounty F0 en la tienda (ADR 236 de CardVault).
 *
 * Cuando una lista no encuentra una carta, el comprador "pone un Bounty" y el
 * operador de su región la consigue con su red de dealers.
 *
 * Todo se cobra en UN solo pago (decisión del owner, 8-oct-2026): las cartas
 * disponibles + el apartado del Bounty (tope + recompensa completos). Lo que no
 * se consiga, o la diferencia si sale más barato, regresa al Saldo de The
 * Vault. El tema arma el pedido y manda a pagar; los montos los calcula SIEMPRE
 * el servidor (`/api/bounties/checkout`) — aquí nunca viaja un precio.
 *
 * Este archivo tiene tres capas, de abajo hacia arriba:
 *
 *   1. Cuentas puras (planes, rango de precio, vencimiento). Sin DOM ni red:
 *      se prueban con `node` en `docs/pruebas/vault-bounty.nucleo.mjs`.
 *   2. El cliente del API de CardVault (`cotizar` y `pagar`), con la base en UN
 *      solo lugar y un simulador apagado por defecto.
 *   3. La interfaz, con el sistema de diseño de The Vault (chips con tono,
 *      avisos contenidos, superficies): la card de las dos salidas que pinta
 *      el Buscador de Decks en su barra lateral, y el modal "Pon un Bounty".
 *
 * El tema NO manda eventos de producto por el Bounty: `bounty_cotizado` y
 * `bounty_creado` los registra el servidor al recibir cada llamada, y
 * `/api/events` tiene lista blanca (ADR 150) — un evento inventado aquí se
 * tiraría en silencio.
 *
 * Expone `window.VaultBounty`.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * 1. Cuentas puras
   * ------------------------------------------------------------------ */

  /**
   * Los tres planes. La recompensa es lo que gana el operador por conseguirla;
   * el tope, cuánto puede subir el precio de la carta sobre el de referencia.
   * Las claves son las del contrato con CardVault.
   */
  var PLANES = [
    { clave: 'calma', emoji: '🐢', nombre: 'Sin prisa', dias: 30, recompensa: 0.10, tope: 0.10 },
    { clave: 'normal', emoji: '🚀', nombre: 'Normal', dias: 20, recompensa: 0.15, tope: 0.20, destacado: 'Lo más elegido' },
    { clave: 'urgente', emoji: '⚡', nombre: 'Urgente', dias: 15, recompensa: 0.20, tope: 0.30 }
  ];

  var PLAN_POR_DEFECTO = 'normal';

  /** Lo más que acepta el API por solicitud. */
  var MAX_CARTAS = 60;

  var DIA_MS = 24 * 60 * 60 * 1000;

  function planDe(clave) {
    for (var i = 0; i < PLANES.length; i++) {
      if (PLANES[i].clave === clave) return PLANES[i];
    }
    return planDe(PLAN_POR_DEFECTO);
  }

  function precioValido(valor) {
    if (valor === null || valor === undefined || valor === '') return null;
    var numero = Number(valor);
    return isFinite(numero) && numero > 0 ? numero : null;
  }

  /**
   * Lo que pagaría por UNA línea (precio × cantidad):
   *   min = base × (1 + recompensa)
   *   max = base × (1 + tope) × (1 + recompensa)
   * `null` cuando no hay precio base: "precio por confirmar" no es $0.
   */
  function rangoDeCarta(precioBase, cantidad, plan) {
    var base = precioValido(precioBase);
    if (base === null) return null;
    var p = typeof plan === 'string' ? planDe(plan) : plan;
    var copias = Math.max(1, Math.round(Number(cantidad) || 1));
    return {
      min: base * (1 + p.recompensa) * copias,
      max: base * (1 + p.tope) * (1 + p.recompensa) * copias
    };
  }

  /**
   * El rango de toda la solicitud. Las cartas sin precio no rompen la suma: se
   * cuentan aparte en `porConfirmar`, y la interfaz dice cuántas son.
   */
  function rangoTotal(cartas, plan) {
    var total = { min: 0, max: 0, porConfirmar: 0, conPrecio: 0 };
    (cartas || []).forEach(function (carta) {
      var rango = rangoDeCarta(carta.precio_base, carta.cantidad, plan);
      if (!rango) {
        total.porConfirmar += 1;
        return;
      }
      total.conPrecio += 1;
      total.min += rango.min;
      total.max += rango.max;
    });
    return total;
  }

  /** Cuándo vence un Bounty puesto ahora con este plan. */
  function venceAt(plan, ahoraMs) {
    var p = typeof plan === 'string' ? planDe(plan) : plan;
    var desde = typeof ahoraMs === 'number' ? ahoraMs : Date.now();
    return new Date(desde + p.dias * DIA_MS);
  }

  /** "$1,235". Se redondea a pesos: un rango con centavos es ruido. */
  function pesos(valor) {
    var numero = Math.round(Number(valor) || 0);
    try {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency', currency: 'MXN', minimumFractionDigits: 0, maximumFractionDigits: 0
      }).format(numero);
    } catch (e) {
      return '$' + String(numero).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
  }

  /**
   * "miércoles 15 de octubre, 14:30 (hora de México)". Siempre en la zona de
   * México, no en la del navegador: el operador trabaja con esa hora.
   */
  function fechaMx(valor) {
    var fecha = valor instanceof Date ? valor : new Date(valor);
    if (isNaN(fecha.getTime())) return '';
    try {
      var dia = new Intl.DateTimeFormat('es-MX', {
        timeZone: 'America/Mexico_City', weekday: 'long', day: 'numeric', month: 'long'
      }).format(fecha);
      var hora = new Intl.DateTimeFormat('es-MX', {
        timeZone: 'America/Mexico_City', hour: '2-digit', minute: '2-digit', hour12: false
      }).format(fecha);
      return dia + ', ' + hora + ' (hora de México)';
    } catch (e) {
      return fecha.toISOString();
    }
  }

  /** El cuerpo que espera `cotizar` (y la base del de `pagar`). Recorta a 60 cartas. */
  function cuerpoDeSolicitud(origen, plan, cartas, acepta) {
    return {
      origen: origen === 'busqueda' ? 'busqueda' : 'lista',
      plan: planDe(plan).clave,
      cartas: (cartas || []).slice(0, MAX_CARTAS).map(function (carta, i) {
        var linea = {
          nombre: String(carta.nombre || ''),
          cantidad: Math.max(1, Math.round(Number(carta.cantidad) || 1)),
          acepta: {
            edicion: Boolean(acepta && acepta[i] && acepta[i].edicion),
            idioma: Boolean(acepta && acepta[i] && acepta[i].idioma),
            lp: Boolean(acepta && acepta[i] && acepta[i].lp)
          }
        };
        if (carta.shopify_product_id) linea.shopify_product_id = String(carta.shopify_product_id);
        if (carta.set) linea.set = String(carta.set);
        if (carta.numero) linea.numero = String(carta.numero);
        return linea;
      })
    };
  }

  /** `gid://shopify/ProductVariant/123` → `123`. Igual que `DeckBuilder.idNumerico`. */
  function idNumerico(id) {
    return String(id === null || id === undefined ? '' : id)
      .replace(/^gid:\/\/shopify\/(ProductVariant|Product)\//, '');
  }

  /**
   * El cuerpo de `/api/bounties/checkout`: el mismo de la solicitud + el
   * carrito de las disponibles. El carrito lleva SOLO variante y cantidad —
   * nunca un precio: el servidor recalcula todo con lo que hay en la bóveda.
   */
  function cuerpoDeCheckout(origen, plan, cartas, acepta, carrito, saldo) {
    var cuerpo = cuerpoDeSolicitud(origen, plan, cartas, acepta);
    // ADR 256: `true` = todo el que alcance; un número = ese monto (Cambiar).
    // Solo se manda si hay algo que usar: el servidor lo topa a su total.
    if (saldo && saldo.usar && Number(saldo.disponible) > 0) {
      cuerpo.saldo = saldo.monto == null ? true : Math.round(Number(saldo.monto) * 100) / 100;
    }
    cuerpo.carrito = (carrito || []).map(function (linea) {
      return {
        variant_id: idNumerico(linea && linea.variant_id),
        cantidad: Math.max(1, Math.round(Number(linea && linea.cantidad) || 1))
      };
    }).filter(function (linea) { return /^\d+$/.test(linea.variant_id); });
    return cuerpo;
  }

  /**
   * Lo que se paga hoy, en pesos:
   *   hoy      las disponibles (lo que ya cuesta el carrito)
   *   apartado el máximo del rango del plan (tope + recompensa completos)
   *   total    hoy + apartado
   * Las cartas sin precio no se apartan: se cuentan en `porConfirmar` y se
   * cobran con su propio link cuando el operador las consiga.
   */
  function totalAPagar(montoHoy, cartas, plan) {
    var hoy = Math.max(0, Number(montoHoy) || 0);
    var rango = rangoTotal(cartas, plan);
    var copiasConPrecio = (cartas || []).reduce(function (suma, carta) {
      return precioValido(carta.precio_base) === null
        ? suma
        : suma + Math.max(1, Math.round(Number(carta.cantidad) || 1));
    }, 0);
    return {
      hoy: hoy,
      apartado: rango.max,
      total: hoy + rango.max,
      copiasConPrecio: copiasConPrecio,
      porConfirmar: rango.porConfirmar
    };
  }

  /**
   * Junta lo que pidió la tienda con lo que contestó `cotizar`. Por posición
   * cuando el servidor respeta el orden; si no, por nombre.
   */
  function unirCotizacion(cartas, cotizacion) {
    var respuesta = cotizacion && Array.isArray(cotizacion.cartas) ? cotizacion.cartas : [];
    return (cartas || []).map(function (carta, i) {
      var nombre = String(carta.nombre || '').toLowerCase();
      var entrada = respuesta[i] && String(respuesta[i].nombre || '').toLowerCase() === nombre
        ? respuesta[i]
        : respuesta.filter(function (r) { return String(r.nombre || '').toLowerCase() === nombre; })[0];
      entrada = entrada || {};
      return {
        nombre: carta.nombre,
        cantidad: carta.cantidad,
        set: carta.set || null,
        numero: carta.numero || null,
        shopify_product_id: carta.shopify_product_id || null,
        precio_base: precioValido(entrada.precio_base),
        precio_top: precioValido(entrada.precio_top),
        imagen: entrada.imagen || carta.imagen || null,
        otra_version: entrada.otra_version || null,
        ya_la_tienes: entrada.ya_la_tienes === true
      };
    });
  }

  function totalDeCopias(cartas) {
    return (cartas || []).reduce(function (suma, carta) {
      return suma + Math.max(1, Math.round(Number(carta.cantidad) || 1));
    }, 0);
  }

  /* ------------------------------------------------------------------ *
   * Seguridad de lo que se interpola
   * ------------------------------------------------------------------ */

  function esc(valor) {
    return String(valor === null || valor === undefined ? '' : valor)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Solo enlaces nuestros. `url_seguimiento` y `otra_version.url` vienen del
   * servidor, pero un `javascript:` en un href es un XSS aunque esté escapado.
   */
  function urlSegura(url) {
    var texto = String(url || '').trim();
    if (/^\/(?!\/)/.test(texto)) return texto;
    if (/^https:\/\/(cards\.)?the-vault\.mx(\/|$)/.test(texto)) return texto;
    return null;
  }

  /**
   * El link de pago que devuelve `/checkout` es el invoice de un draft order
   * de Shopify. Shopify lo arma con el dominio principal de la tienda
   * (`https://the-vault.mx/<shop id>/invoices/<token>`) y, si no lo hubiera,
   * con el `.myshopify.com` de la tienda. Solo esos dos hosts y solo la ruta
   * `/invoices/`: un link de pago a cualquier otro lado no se sigue.
   */
  var HOSTS_DE_PAGO = ['the-vault.mx', 'www.the-vault.mx', 'f34c9b-7b.myshopify.com'];

  function urlDePago(url) {
    var texto = String(url || '').trim();
    var partes = /^https:\/\/([a-z0-9.-]+)(\/(?:\d+\/)?invoices\/[A-Za-z0-9_-]+)(\?[^#\s]*)?$/.exec(texto);
    if (!partes) return null;
    return HOSTS_DE_PAGO.indexOf(partes[1]) === -1 ? null : texto;
  }

  /** Imágenes solo por https (o relativas de protocolo del CDN de Shopify). */
  function imagenSegura(url) {
    var texto = String(url || '').trim();
    if (/^https:\/\//.test(texto) || /^\/\/cdn\.shopify\.com\//.test(texto)) return texto;
    return null;
  }

  /**
   * Toda carta se ve. Si ni el catálogo ni `cotizar` traen foto, se le pide a
   * Scryfall por nombre (y por set cuando la lista lo traía): nunca solo el
   * nombre en una caja vacía.
   */
  function imagenDe(carta) {
    var propia = imagenSegura(carta.imagen);
    if (propia) return propia;
    var url = 'https://api.scryfall.com/cards/named?format=image&version=normal&exact=' +
      encodeURIComponent(carta.nombre || '');
    if (carta.set) url += '&set=' + encodeURIComponent(String(carta.set).toLowerCase());
    return url;
  }

  function versionDe(carta) {
    if (carta.set && carta.numero) return carta.set + ' · ' + carta.numero;
    if (carta.set) return carta.set;
    return 'Cualquier impresión';
  }

  /* ------------------------------------------------------------------ *
   * 2. Cliente del API
   * ------------------------------------------------------------------ */

  /**
   * La base del API vive aquí y solo aquí. Se resuelve al llamar —no al
   * cargar— porque `vault-identidad.js` se carga después de las secciones, y
   * es quien ya sabe si estamos en producción o en el proxy local.
   */
  function apiBase() {
    if (window.VaultIdentidad && window.VaultIdentidad.origen) return window.VaultIdentidad.origen;
    return 'https://cards.the-vault.mx';
  }

  /**
   * Simulador para probar el modal sin backend. APAGADO: solo se prende en el
   * servidor local del tema (`shopify theme dev`) y con `?bounty_mock=1` en la
   * URL. `?bounty_mock=401` simula "sin sesión", `409` "se acaba de agotar" y
   * `500` una falla. En la tienda de verdad no hay forma de encenderlo, y en
   * el simulador el pago NUNCA redirige: se enseña un recibo simulado.
   */
  function modoSimulado() {
    var local = window.location.port === '9292' || window.location.hostname === '127.0.0.1' ||
      window.location.hostname === 'localhost';
    if (!local) return null;
    var valor = new URLSearchParams(window.location.search).get('bounty_mock');
    return valor || null;
  }

  /** Precio inventado y estable por carta (cada cuarta va "por confirmar"). */
  function precioSimulado(carta, i) {
    return i % 4 === 3 ? null : 40 + (String(carta.nombre).length * 37) % 900;
  }

  function simular(ruta, cuerpo) {
    var modo = modoSimulado();
    var varianteSimulada = new URLSearchParams(window.location.search).get('bounty_mock_variante');
    return new Promise(function (resolver) {
      setTimeout(function () {
        if (ruta === '/api/bounties/checkout') {
          if (modo === '401') return resolver({ ok: false, status: 401, datos: { error: 'sin_sesion' } });
          if (modo === '500') return resolver({ ok: false, status: 500, datos: { error: 'interno' } });
          if (modo === '409') {
            var primera = (cuerpo.carrito || [])[0];
            return resolver({
              ok: false,
              status: 409,
              datos: { error: 'sin_stock', variantes: primera ? [primera.variant_id] : [] }
            });
          }
          var plan = planDe(cuerpo.plan);
          var apartado = cuerpo.cartas.reduce(function (suma, carta, i) {
            var rango = rangoDeCarta(precioSimulado(carta, i), carta.cantidad, plan);
            return rango ? suma + rango.max : suma;
          }, 0);
          var hayCobro = apartado > 0 || (cuerpo.carrito || []).length > 0;
          // ADR 256: el Saldo simulado. El total del simulador es solo el
          // apartado (no conoce el precio de las disponibles).
          var saldoMock = Number(new URLSearchParams(window.location.search).get('bounty_mock_saldo')) || 0;
          var usado = !cuerpo.saldo ? 0 : Math.min(cuerpo.saldo === true ? saldoMock : cuerpo.saldo, saldoMock, apartado);
          var pagadoMock = usado > 0 && usado >= apartado && !(cuerpo.carrito || []).length;
          return resolver({
            ok: true,
            status: 201,
            datos: {
              id: 'simulado',
              codigo: 'B-0142',
              invoice_url: hayCobro && !pagadoMock ? 'https://the-vault.mx/66472181944/invoices/simulado' : null,
              pagado: pagadoMock,
              saldo_usado: usado,
              total_a_pagar: Math.max(0, apartado - usado),
              apartado: apartado,
              sin_precio: cuerpo.cartas.filter(function (_, i) { return precioSimulado(cuerpo.cartas[i], i) === null; }).length,
              vence_pago_at: new Date(Date.now() + DIA_MS).toISOString(),
              vence_at: venceAt(plan).toISOString(),
              url_seguimiento: 'https://cards.the-vault.mx/dashboard'
            }
          });
        }
        resolver({
          ok: true,
          status: 200,
          datos: {
            plan: planDe(cuerpo.plan),
            cartas: cuerpo.cartas.map(function (carta, i) {
              var base = precioSimulado(carta, i);
              return {
                nombre: carta.nombre,
                precio_base: base,
                precio_top: base === null ? null : base * (1 + planDe(cuerpo.plan).tope),
                imagen: null,
                // `&bounty_mock_variante=123` (variante real de la tienda) prueba "Agregar esta".
                otra_version: i === 0 ? {
                  titulo: 'Commander Masters',
                  precio: 85,
                  url: varianteSimulada ? '/products/simulada?variant=' + varianteSimulada : '/search?q=' + encodeURIComponent(carta.nombre)
                } : null,
                ya_la_tienes: i === 1
              };
            }),
            rango: { min: 0, max: 0 },
            vence_at: venceAt(cuerpo.plan).toISOString(),
            // `&bounty_mock_saldo=500` enciende "Usar mi saldo".
            saldo_disponible: Number(new URLSearchParams(window.location.search).get('bounty_mock_saldo')) || 0
          }
        });
      }, 400);
    });
  }

  function pedir(ruta, cuerpo) {
    if (modoSimulado()) return simular(ruta, cuerpo);

    return fetch(apiBase() + ruta, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(cuerpo)
    })
      .then(function (respuesta) {
        return respuesta.json()
          .catch(function () { return null; })
          .then(function (datos) { return { ok: respuesta.ok, status: respuesta.status, datos: datos }; });
      })
      .catch(function () {
        return { ok: false, status: 0, datos: null };
      });
  }

  function cotizar(cuerpo) {
    return pedir('/api/bounties/cotizar', cuerpo);
  }

  /**
   * Pagar sí necesita saber quién eres: el Bounty y el pago quedan a tu nombre.
   * La identidad va firmada en la cookie `vault_auth` (ADR 216), que
   * `VaultIdentidad.asegurar()` pide si falta —el mismo paso que da Make an
   * Offer—. Sin sesión, el servidor contesta 401 `sin_sesion` y el modal
   * ofrece iniciarla.
   */
  function pagar(cuerpo) {
    var listo = window.VaultIdentidad && typeof window.VaultIdentidad.asegurar === 'function'
      ? window.VaultIdentidad.asegurar().catch(function () { return false; })
      : Promise.resolve(false);
    return listo.then(function () { return pedir('/api/bounties/checkout', cuerpo); });
  }

  /** Qué pasó y qué hacer. Sin disculpas. */
  function mensajeDeError(status, datos) {
    var detalle = datos && typeof datos.error === 'string' ? datos.error : '';
    if (status === 0) return 'No hubo conexión con The Vault. Revisa tu internet y vuelve a intentarlo.';
    // Piloto (16-oct): Bounty solo en algunas regiones; el servidor dice cuáles.
    if (status === 403 && detalle === 'fuera_del_piloto') {
      return typeof datos.mensaje === 'string' && datos.mensaje ? datos.mensaje : 'Por ahora Bounty solo funciona en CDMX. Pronto llega a más estados.';
    }
    if (status === 400 || status === 422) {
      return 'La solicitud no pasó la revisión' + (detalle ? ' (' + detalle + ')' : '') +
        '. Revisa las cartas y vuelve a intentarlo; si se repite, escríbenos por WhatsApp.';
    }
    if (status === 429) return 'Hiciste varias solicitudes seguidas. Espera un minuto y vuelve a intentarlo.';
    if (status === 404) return 'El servicio de Bounties todavía no está disponible. Vuelve a intentarlo más tarde.';
    return 'El servidor no pudo armar tu pago (error ' + status + '). No se cobró nada: vuelve a intentarlo en unos minutos; si se repite, escríbenos por WhatsApp.';
  }


  /* ------------------------------------------------------------------ *
   * 3. Piezas del sistema de diseño
   *
   * Íconos de trazo (lucide, `currentColor`), chips con tono y avisos
   * contenidos. Los tonos los define `vault-bounty.css` como `--vb-tono-*`,
   * espejo de `components/ui/badge.tsx` de CardVault. Todo texto que entra
   * aquí ya viene escapado por quien llama.
   * ------------------------------------------------------------------ */

  var ICONOS = {
    check: '<path d="M20 6 9 17l-5-5"/>',
    reloj: '<circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>',
    buscar: '<circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>',
    alerta: '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><path d="M12 9v4"/><path d="M12 17h.01"/>',
    info: '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>',
    boveda: '<rect width="20" height="5" x="2" y="3" rx="1"/><path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8"/><path d="M10 12h4"/>',
    etiqueta: '<path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"/><circle cx="7.5" cy="7.5" r=".5"/>',
    capas: '<path d="M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z"/><path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65"/><path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65"/>',
    error: '<circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/>',
    entrar: '<path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><path d="m10 17 5-5-5-5"/><path d="M15 12H3"/>',
    calendario: '<rect width="18" height="18" x="3" y="4" rx="2"/><path d="M16 2v4"/><path d="M8 2v4"/><path d="M3 10h18"/>',
    monedas: '<circle cx="8" cy="8" r="6"/><path d="M18.09 10.37A6 6 0 1 1 10.34 18"/><path d="M7 6h1v4"/><path d="m16.71 13.88.7.71-2.82 2.82"/>',
    sube: '<path d="M22 7 13.5 15.5 8.5 10.5 2 17"/><path d="M16 7h6v6"/>',
    estrella: '<path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z"/>',
    flecha: '<path d="M5 12h14"/><path d="m13 6 6 6-6 6"/>',
    cargando: '<path d="M21 12a9 9 0 1 1-6.219-8.56"/>',
    abajo: '<path d="m6 9 6 6 6-6"/>',
    cerrar: '<path d="M18 6 6 18"/><path d="m6 6 12 12"/>',
    quitar: '<path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><path d="M10 11v6"/><path d="M14 11v6"/>',
    regresar: '<path d="M9 14 4 9l5-5"/><path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11"/>',
    mas: '<path d="M5 12h14"/><path d="M12 5v14"/>',
    menos: '<path d="M5 12h14"/>',
    atras: '<path d="m12 19-7-7 7-7"/><path d="M19 12H5"/>'
  };

  function icono(nombre, tam) {
    var lado = tam || 14;
    return '<svg class="vb-icono" width="' + lado + '" height="' + lado + '" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
      (ICONOS[nombre] || '') + '</svg>';
  }

  /** Chip con tono: success | info | warning | pending | neutral | danger. `html` ya escapado. */
  function chip(tono, nombreIcono, html, atributos) {
    return '<span class="vb-chip vb-chip--' + tono + '"' + (atributos || '') + '>' +
      (nombreIcono ? icono(nombreIcono, 13) : '') + '<span>' + html + '</span></span>';
  }

  /** Aviso contenido con tono (el resultado nunca va en un párrafo suelto). `html` ya escapado. */
  function aviso(tono, nombreIcono, html, atributos, claseExtra) {
    return '<div class="vb-aviso vb-aviso--' + tono + (claseExtra ? ' ' + claseExtra : '') + '"' + (atributos || '') + '>' +
      icono(nombreIcono, 18) + '<div class="vb-aviso__texto">' + html + '</div></div>';
  }

  /* ------------------------------------------------------------------ *
   * 3a. Lo que pinta el Buscador de Decks
   * ------------------------------------------------------------------ */

  /**
   * El aviso de que ya hay otra versión aquí, como chip info con su enlace. Va
   * en la fila de la faltante —en la lista y en el modal— antes de pedir que
   * la consigan.
   */
  function htmlOtraVersion(otra, atributosDeAccion) {
    if (!otra || !otra.titulo) return '';
    var precio = precioValido(otra.precio);
    var texto = 'Tenemos otra versión' + (precio !== null ? ' en ' + esc(pesos(precio)) : '') +
      ' · ' + esc(otra.titulo);
    var cuerpo = icono('capas', 13) + '<span>' + texto + '</span>';
    // Con acción y variante conocida, se agrega ahí mismo: sacar al comprador
    // a la ficha del producto lo saca del flujo de compra.
    if (atributosDeAccion && varianteDeOtra(otra)) {
      return '<button type="button" class="vb-chip vb-chip--info vault-bounty-otra vault-bounty-otra--accion"' + atributosDeAccion + '>' +
        cuerpo + '<span class="vault-bounty-otra__cta">' + icono('mas', 13) + 'Agregar esta</span></button>';
    }
    var url = urlSegura(otra.url);
    return url
      ? '<a class="vb-chip vb-chip--info vault-bounty-otra" href="' + esc(url) + '">' + cuerpo + '</a>'
      : '<span class="vb-chip vb-chip--info vault-bounty-otra">' + cuerpo + '</span>';
  }

  /**
   * La variante de la otra versión: la manda el API en `variant_id`, o viene
   * en su enlace a la ficha (`?variant=123`). Sin variante no se puede agregar.
   */
  function varianteDeOtra(otra) {
    if (!otra) return null;
    var directa = idNumerico(otra.variant_id || '');
    if (/^\d{1,20}$/.test(directa)) return directa;
    var enUrl = /[?&]variant=(\d{1,20})/.exec(String(otra.url || ''));
    return enUrl ? enUrl[1] : null;
  }

  /**
   * Lo que costaría lo que falta con el plan por defecto, si ya hay cotización
   * y al menos una carta trae precio. `null` = "por cotizar".
   */
  function estimadoFaltantes(cartas, cotizacion) {
    if (!cotizacion) return null;
    var total = rangoTotal(unirCotizacion(cartas, cotizacion), PLAN_POR_DEFECTO);
    return total.conPrecio > 0 ? total : null;
  }

  /**
   * Las dos salidas de la lista, en la barra lateral arriba del botón de
   * carrito: una card con lo que hay (chip success + monto de hoy) y lo que
   * falta (chip pending + estimado), y UN CTA primary que lleva a un solo
   * pago. El botón de carrito de siempre queda abajo como secundario.
   *
   *   opciones.cartas      las faltantes (como `cartasParaBounty`)
   *   opciones.disponibles unidades que irían al carrito
   *   opciones.montoHoy    lo que cuestan esas unidades, en pesos
   *   opciones.cotizacion  la respuesta de `cotizar`, si ya llegó
   */
  function htmlSalidas(opciones) {
    var cartas = (opciones && opciones.cartas) || [];
    if (cartas.length === 0) return '';
    var faltan = totalDeCopias(cartas);
    var hay = Math.max(0, Math.round(Number(opciones.disponibles) || 0));
    var estimado = estimadoFaltantes(cartas, opciones.cotizacion);
    var pronombre = faltan === 1 ? 'la' : 'las';

    var miniaturas = cartas.slice(0, 4).map(function (carta, n) {
      return '<span class="vb-banner__carta" style="--i:' + n + '"><img src="' + esc(imagenDe(carta)) + '" alt="" decoding="async" loading="lazy"></span>';
    }).join('');

    // Sin precio: el banner invita a entrar; los montos se ven en el configurador.
    var detalle = hay > 0
      ? hay + ' hoy + ' + faltan + ' te ' + pronombre + ' conseguimos · un solo pago'
      : 'Te ' + pronombre + ' conseguimos · lo que sobre regresa a tu Saldo';

    // Botón-banner: es la salida nueva y se ve distinta del botón de carrito,
    // que sigue siendo el primary ("Me llevo solo lo que hay").
    return '' +
      '<button type="button" class="vb-banner" ' + (hay > 0 ? 'data-bounty-completar data-foco="bounty-completar"' : 'data-bounty-abrir data-foco="bounty-abrir"') + '>' +
        '<span class="vb-banner__abanico" aria-hidden="true">' + miniaturas + '</span>' +
        '<span class="vb-banner__texto">' +
          '<span class="vb-banner__eyebrow">' + icono('estrella', 12) + '<span>Bounty · nuevo</span></span>' +
          '<span class="vb-banner__titulo">' + (faltan === 1 ? 'Consíguela' : 'Consíguelas todas') + '</span>' +
          '<span class="vb-banner__detalle">' + esc(detalle) + '</span>' +
        '</span>' +
        '<span class="vb-banner__flecha" aria-hidden="true">' + icono('flecha', 18) + '</span>' +
      '</button>';
  }

  /* ------------------------------------------------------------------ *
   * 3b. El modal
   * ------------------------------------------------------------------ */

  var modal = null;
  var estado = null;

  var CHIPS = [
    { clave: 'edicion', texto: 'Otra edición' },
    { clave: 'idioma', texto: 'Otro idioma' },
    { clave: 'lp', texto: 'Lightly Played' }
  ];

  function asegurarModal() {
    if (modal) return modal;
    modal = document.createElement('dialog');
    modal.className = 'vault-bounty';
    modal.setAttribute('aria-labelledby', 'vault-bounty-titulo');
    document.body.appendChild(modal);

    // Clic en el fondo = cerrar. El `<dialog>` ocupa la pantalla y el contenido
    // vive en `.vault-bounty__caja`: un clic cuyo objetivo es el dialog mismo
    // cayó fuera de la caja.
    modal.addEventListener('click', alHacerClic);
    modal.addEventListener('change', alCambiar);
    modal.addEventListener('input', function (evento) {
      var campo = evento.target.closest('input[data-bounty-buscar]');
      if (campo) alEscribirBusqueda(campo.value);
    });
    modal.addEventListener('keydown', atraparFoco);
    modal.addEventListener('close', function () {
      document.documentElement.style.overflow = '';
      if (estado && estado.devolverFoco && typeof estado.devolverFoco.focus === 'function') {
        estado.devolverFoco.focus();
      }
    });
    return modal;
  }

  function cerrarModal() {
    if (typeof modal.close === 'function') {
      modal.close();
      return;
    }
    modal.removeAttribute('open');
    modal.dispatchEvent(new Event('close'));
  }

  /** "Para todas": un criterio global, que escribe en cada carta. */
  /** Lo que sigue en el Bounty: `lista` sin las que se quitaron en el modal. */
  function vivas(lista) {
    return (lista || []).filter(function (_, i) { return !estado.quitadas[i]; });
  }

  /**
   * Quitar o regresar una carta sin repintar el modal: una caja nueva corre la
   * animación de entrada y pierde el scroll. Se cambia su renglón, la cuenta y
   * las cifras; el foco cae en el botón contrario de la misma carta.
   */
  function alternarQuitada(indice, quitada) {
    if (!estado.cartas[indice] || estado.fase === 'enviando') return;
    estado.quitadas[indice] = quitada;
    refrescarRenglon(indice, '[data-bounty-' + (quitada ? 'regresar' : 'quitar') + '="' + indice + '"]');
  }

  /** Sin nada que buscar: las disponibles van al carrito de siempre y de ahí a pagar. */
  function llevarAlCarrito() {
    if (estado.fase === 'enviando') return;
    estado.fase = 'enviando';
    pintarPie();
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: estado.carrito.map(function (l) { return { id: Number(idNumerico(l.variant_id)), quantity: l.cantidad }; })
      })
    }).then(function (r) {
      if (r.ok) {
        window.location.href = '/cart';
        return;
      }
      estado.fase = 'formulario';
      estado.error = 'No pudimos agregarlas a tu carrito. Revisa que sigan disponibles y vuelve a intentarlo.';
      pintarPie();
    }).catch(function () {
      estado.fase = 'formulario';
      estado.error = 'No hubo conexión. Revisa tu internet y vuelve a intentarlo.';
      pintarPie();
    });
  }

  /** Repinta un renglón, la cuenta y las cifras sin repintar el modal; el foco va a `selectorFoco`. */
  function refrescarRenglon(indice, selectorFoco) {
    var renglon = modal.querySelector('[data-bounty-renglon="' + indice + '"]');
    if (renglon) renglon.outerHTML = htmlCarta(estado.cartas[indice], indice);
    if (!estado.quitadas[indice]) marcarAjustada(indice);
    var cuenta = modal.querySelector('[data-bounty-cuenta]');
    if (cuenta) cuenta.textContent = vivas(estado.cartas).length;
    var porConseguir = modal.querySelector('[data-bounty-por-conseguir]');
    if (porConseguir) porConseguir.textContent = totalDeCopias(vivas(estado.cartasPedidas)) + ' por conseguir';
    actualizarCifras();
    var foco = selectorFoco && modal.querySelector(selectorFoco);
    if (foco && !foco.disabled) foco.focus();
  }

  /**
   * "Agregar esta": la carta sale del Bounty y la versión que sí tenemos entra
   * al mismo pago, con las mismas copias. Si no alcanza el stock, el checkout
   * lo dice (409 sin_stock) como con cualquier disponible.
   */
  function cambiarPorOtra(indice) {
    var carta = estado.cartas[indice];
    var variante = carta && varianteDeOtra(carta.otra_version);
    if (!variante || estado.quitadas[indice] || estado.fase === 'enviando') return;
    var linea = { variant_id: variante, cantidad: carta.cantidad, nombre: carta.nombre + ' · ' + carta.otra_version.titulo };
    estado.carrito = estado.carrito.concat([linea]);
    estado.disponibles += linea.cantidad;
    estado.montoHoy += (precioValido(carta.otra_version.precio) || 0) * linea.cantidad;
    estado.cambiadas[indice] = linea;
    estado.quitadas[indice] = true;
    refrescarRenglon(indice, '[data-bounty-regresar="' + indice + '"]');
    pintarNotaDisponibles();
  }

  /** Deshacer el cambio: la línea sale del pago y la carta regresa al Bounty. */
  function deshacerCambio(indice) {
    var linea = estado.cambiadas[indice];
    if (!linea) return false;
    var carta = estado.cartas[indice];
    estado.carrito = estado.carrito.filter(function (l) { return l !== linea; });
    estado.disponibles = Math.max(0, estado.disponibles - linea.cantidad);
    estado.montoHoy = Math.max(0, estado.montoHoy - (precioValido(carta.otra_version && carta.otra_version.precio) || 0) * linea.cantidad);
    delete estado.cambiadas[indice];
    // La que entró directo del buscador nunca estuvo en el Bounty: se va.
    if (linea.directa) estado.ocultas[indice] = true;
    return linea.directa === true;
  }

  function pintarNotaDisponibles() {
    var nota = modal.querySelector('[data-bounty-nota-disponibles]');
    if (nota) nota.outerHTML = htmlNotaDisponibles();
  }

  function htmlNotaDisponibles() {
    if (!(estado.disponibles > 0)) return '<span data-bounty-nota-disponibles hidden></span>';
    return '<p class="vb-pasos__nota" data-bounty-nota-disponibles>' + icono('check', 13) + '<span>' +
      (estado.disponibles === 1 ? 'Tu carta disponible va' : 'Tus ' + estado.disponibles + ' cartas disponibles van') +
      ' en el mismo pago.</span></p>';
  }

  /** Lo más que acepta el API por carta. */
  var MAX_CANTIDAD = 20;

  /** − / + en el paso 2: la cantidad de una carta, entre 1 y 20. */
  function cambiarCantidad(indice, delta) {
    var carta = estado.cartas[indice];
    if (!carta || estado.quitadas[indice] || estado.fase === 'enviando') return;
    var nueva = Math.max(1, Math.min(MAX_CANTIDAD, (Number(carta.cantidad) || 1) + delta));
    if (nueva === carta.cantidad) return;
    carta.cantidad = nueva;
    estado.cartasPedidas[indice] = Object.assign({}, estado.cartasPedidas[indice], { cantidad: nueva });
    refrescarRenglon(indice, '[data-bounty-cantidad="' + indice + ':' + delta + '"]');
  }

  /* ---------------- "+ Agregar carta": el buscador del paso 2 ---------------- */

  /** "Absorb [Dominaria Remastered]" → "Absorb": la edición viaja en el producto. */
  function nombreSinEdicion(titulo) {
    var limpio = String(titulo || '').replace(/\s*\[[^\]]*\]\s*$/, '').trim();
    return limpio || String(titulo || '').trim();
  }

  /**
   * La búsqueda predictiva de la tienda (la misma del buscador del header):
   * productos que se parecen a lo escrito. Devuelve [] si no contesta.
   */
  function buscarEnLaTienda(q) {
    var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
      '&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last';
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (datos) {
        var productos = datos && datos.resources && datos.resources.results && datos.resources.results.products;
        return (Array.isArray(productos) ? productos : []).map(function (p) {
          return {
            nombre: nombreSinEdicion(p.title),
            titulo: String(p.title || ''),
            shopify_product_id: p.id ? String(p.id) : null,
            imagen: imagenSegura((p.featured_image && p.featured_image.url) || p.image || null),
            // En venta hoy: va al pago de hoy, no al Bounty.
            disponible: p.available === true && typeof p.handle === 'string',
            handle: typeof p.handle === 'string' ? p.handle : null,
            precio: precioValido(p.price_min !== undefined ? p.price_min : p.price)
          };
        }).filter(function (p) { return p.nombre; });
      })
      .catch(function () { return []; });
  }

  function abrirBuscador() {
    if (estado.fase === 'enviando') return;
    estado.buscador = { q: '', resultados: [], cargando: false, ronda: 0 };
    pintar();
    var campo = modal.querySelector('input[data-bounty-buscar]');
    if (campo) campo.focus();
  }

  function cerrarBuscador(indiceNuevo) {
    estado.buscador = null;
    pintar();
    var destino = indiceNuevo === undefined ? null : modal.querySelector('[data-bounty-renglon="' + indiceNuevo + '"]');
    if (destino) {
      destino.scrollIntoView({ block: 'nearest' });
      var foco = destino.querySelector('[data-bounty-cantidad$=":1"]');
      if (foco) foco.focus();
    } else {
      var boton = modal.querySelector('[data-bounty-agregar]');
      if (boton) boton.focus();
    }
  }

  var esperaDeBusqueda = null;
  function alEscribirBusqueda(q) {
    var b = estado.buscador;
    if (!b) return;
    b.q = q;
    clearTimeout(esperaDeBusqueda);
    if (q.trim().length < 2) {
      b.resultados = [];
      b.cargando = false;
      pintarResultados();
      return;
    }
    b.cargando = true;
    pintarResultados();
    var ronda = ++b.ronda;
    esperaDeBusqueda = setTimeout(function () {
      buscarEnLaTienda(q.trim()).then(function (resultados) {
        if (estado.buscador !== b || b.ronda !== ronda) return;
        b.resultados = resultados;
        b.cargando = false;
        pintarResultados();
      });
    }, 250);
  }

  /**
   * Elegir un resultado (o el nombre tal cual): si esa carta ya estaba, se le
   * suma una copia (y si estaba quitada, regresa); si no, entra al final y se
   * cotiza sola, con el plan elegido.
   */
  function elegirCarta(carta) {
    if (carta.disponible && carta.handle) {
      elegirDisponible(carta);
      return;
    }
    var nombre = String(carta.nombre || '').toLowerCase();
    var existente = -1;
    estado.cartas.forEach(function (c, i) {
      if (existente !== -1) return;
      var mismoProducto = carta.shopify_product_id && c.shopify_product_id && String(c.shopify_product_id) === String(carta.shopify_product_id);
      if (mismoProducto || (!carta.shopify_product_id && String(c.nombre || '').toLowerCase() === nombre)) existente = i;
    });
    if (existente !== -1 && estado.ocultas[existente]) existente = -1;
    if (existente !== -1) {
      var antes = estado.quitadas[existente];
      estado.quitadas[existente] = false;
      cerrarBuscador(existente);
      if (!antes) cambiarCantidad(existente, 1);
      else refrescarRenglon(existente);
      return;
    }
    if (estado.cartas.length >= MAX_CARTAS) return;

    var pedida = {
      nombre: carta.nombre,
      cantidad: 1,
      shopify_product_id: carta.shopify_product_id || null,
      imagen: carta.imagen || null
    };
    var indice = estado.cartas.length;
    estado.cartasPedidas.push(pedida);
    estado.acepta.push({ edicion: estado.aceptaGlobal.edicion, idioma: estado.aceptaGlobal.idioma, lp: estado.aceptaGlobal.lp });
    estado.quitadas.push(false);
    estado.cartas.push(unirCotizacion([pedida], null)[0]);
    cerrarBuscador(indice);

    var deEsteModal = estado;
    cotizar(cuerpoDeSolicitud(estado.origen, estado.plan, [pedida])).then(function (r) {
      if (estado !== deEsteModal || !r.ok || !r.datos) return;
      estado.cartas[indice] = unirCotizacion([pedida], r.datos)[0];
      estado.cartas[indice].cantidad = estado.cartasPedidas[indice].cantidad;
      if (!estado.buscador && estado.paso === 'cartas') refrescarRenglon(indice);
    });
  }

  /**
   * Del buscador, una carta que SÍ tenemos en venta: va al pago de hoy con su
   * primera variante disponible (el orden de la tienda pone Near Mint primero).
   * Si ya no hay ninguna disponible, se pide al Bounty como cualquier otra.
   */
  function elegirDisponible(carta) {
    var repetida = -1;
    Object.keys(estado.cambiadas).forEach(function (k) {
      var l = estado.cambiadas[k];
      if (l.directa && l.handle === carta.handle && !estado.ocultas[k]) repetida = Number(k);
    });
    if (repetida !== -1) {
      var linea = estado.cambiadas[repetida];
      linea.cantidad += 1;
      estado.disponibles += 1;
      estado.montoHoy += linea.precio || 0;
      cerrarBuscador(repetida);
      refrescarRenglon(repetida);
      pintarNotaDisponibles();
      return;
    }
    fetch('/products/' + encodeURIComponent(carta.handle) + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; })
      .then(function (producto) {
        if (!estado || !estado.buscador) return;
        var variante = producto && Array.isArray(producto.variants)
          ? producto.variants.filter(function (v) { return v && v.available; })[0]
          : null;
        if (!variante) {
          elegirCarta(Object.assign({}, carta, { disponible: false }));
          return;
        }
        var precio = (Number(variante.price) || 0) / 100;
        var indice = estado.cartas.length;
        var lineaNueva = {
          variant_id: String(variante.id),
          cantidad: 1,
          nombre: carta.titulo + (variante.title && variante.title !== 'Default Title' ? ' · ' + variante.title : ''),
          directa: true,
          handle: carta.handle,
          precio: precio
        };
        estado.cartasPedidas.push({ nombre: carta.nombre, cantidad: 1, shopify_product_id: carta.shopify_product_id, imagen: carta.imagen });
        estado.acepta.push({ edicion: false, idioma: false, lp: false });
        estado.quitadas.push(true);
        estado.cartas.push({
          nombre: carta.nombre,
          cantidad: 1,
          shopify_product_id: carta.shopify_product_id,
          imagen: carta.imagen,
          precio_base: null,
          precio_top: null,
          otra_version: { titulo: lineaNueva.nombre, precio: precio },
          ya_la_tienes: false
        });
        estado.cambiadas[indice] = lineaNueva;
        estado.carrito = estado.carrito.concat([lineaNueva]);
        estado.disponibles += 1;
        estado.montoHoy += precio;
        cerrarBuscador(indice);
        pintarNotaDisponibles();
      });
  }

  function htmlResultados() {
    var b = estado.buscador;
    var q = b.q.trim();
    if (q.length < 2) return '<p class="vb-nota">Escribe el nombre de la carta.</p>';
    var filas = b.resultados.map(function (p, i) {
      return '' +
        '<li><button type="button" class="vb-resultado" data-bounty-elegir="' + i + '">' +
          '<img class="vb-carta__imagen" src="' + esc(imagenDe({ nombre: p.nombre, imagen: p.imagen })) + '" alt="" decoding="async" loading="lazy">' +
          '<span class="vb-resultado__texto"><span class="vb-carta__nombre">' + esc(p.nombre) + '</span>' +
            (p.titulo !== p.nombre ? '<span class="vb-carta__version">' + esc(p.titulo) + '</span>' : '') +
            '<span class="vb-resultado__estado">' +
              (p.disponible
                ? chip('success', 'check', 'La tenemos' + (p.precio !== null ? ' · desde ' + esc(pesos(p.precio)) : '') + ' · va en tu pago de hoy')
                : chip('pending', 'buscar', 'No la tenemos · la buscamos')) +
            '</span>' +
          '</span>' + icono('mas', 16) +
        '</button></li>';
    }).join('');
    return '' +
      (b.cargando && b.resultados.length === 0 ? aviso('neutral', 'cargando', 'Buscando…', ' role="status" data-cargando') : '') +
      (filas ? '<ul class="vb-cartas vb-resultados">' + filas + '</ul>' : '') +
      '<button type="button" class="vb-resultado vb-resultado--tal-cual" data-bounty-elegir="tal-cual">' +
        icono('mas', 16) + '<span>Pedir «' + esc(q) + '» tal cual, en cualquier edición</span>' +
      '</button>';
  }

  function pintarResultados() {
    var caja = modal && modal.querySelector('[data-bounty-resultados]');
    if (caja && estado.buscador) caja.innerHTML = htmlResultados();
  }

  function htmlBuscador() {
    return '' +
      '<section class="vb-bloque" aria-labelledby="vb-paso-titulo">' +
        '<div class="vb-buscador__cabeza">' +
          '<button type="button" class="vb-carta__ajustar" data-bounty-buscador-volver aria-label="Volver a las cartas">' + icono('atras', 16) + '<span>Volver</span></button>' +
          '<h3 class="vb-seccion" id="vb-paso-titulo" tabindex="-1">Agregar carta</h3>' +
        '</div>' +
        '<label class="vb-buscador__campo">' +
          icono('buscar', 16) +
          '<input type="search" data-bounty-buscar autocomplete="off" placeholder="Busca una carta…" aria-label="Buscar una carta para agregar" value="' + esc(estado.buscador.q) + '">' +
        '</label>' +
        '<div class="vb-buscador__resultados" data-bounty-resultados aria-live="polite">' + htmlResultados() + '</div>' +
      '</section>';
  }

  function alternarGlobal(clave) {
    var valor = !estado.aceptaGlobal[clave];
    estado.aceptaGlobal[clave] = valor;
    estado.acepta.forEach(function (acepta) { acepta[clave] = valor; });
    Array.prototype.forEach.call(modal.querySelectorAll('[data-bounty-chip$=":' + clave + '"]'), function (nodo) {
      nodo.setAttribute('aria-pressed', valor ? 'true' : 'false');
    });
    var global = modal.querySelector('[data-bounty-global="' + clave + '"]');
    if (global) global.setAttribute('aria-pressed', valor ? 'true' : 'false');
    estado.acepta.forEach(function (_, i) { marcarAjustada(i); });
  }

  /** El chip "Ajustada" dice qué cartas se apartan del criterio global. */
  function marcarAjustada(indice) {
    var nodo = modal.querySelector('[data-bounty-ajustada="' + indice + '"]');
    if (!nodo) return;
    var propia = estado.acepta[indice];
    nodo.hidden = !CHIPS.some(function (c) { return Boolean(propia[c.clave]) !== Boolean(estado.aceptaGlobal[c.clave]); });
  }

  function alHacerClic(evento) {
    if (evento.target === modal) {
      cerrarModal();
      return;
    }
    if (evento.target.closest('[data-bounty-cerrar]')) {
      cerrarModal();
      return;
    }
    var global = evento.target.closest('[data-bounty-global]');
    if (global) {
      alternarGlobal(global.dataset.bountyGlobal);
      return;
    }
    var ajustar = evento.target.closest('[data-bounty-ajustar]');
    if (ajustar) {
      var panel = modal.querySelector('#' + ajustar.getAttribute('aria-controls'));
      if (!panel) return;
      var abrirlo = panel.hidden;
      panel.hidden = !abrirlo;
      ajustar.setAttribute('aria-expanded', abrirlo ? 'true' : 'false');
      return;
    }
    var cantidad = evento.target.closest('[data-bounty-cantidad]');
    if (cantidad) {
      var partesCantidad = cantidad.dataset.bountyCantidad.split(':');
      cambiarCantidad(Number(partesCantidad[0]), Number(partesCantidad[1]));
      return;
    }
    if (evento.target.closest('[data-bounty-agregar]')) {
      abrirBuscador();
      return;
    }
    if (evento.target.closest('[data-bounty-buscador-volver]')) {
      cerrarBuscador();
      return;
    }
    var elegida = evento.target.closest('[data-bounty-elegir]');
    if (elegida && estado.buscador) {
      var cual = elegida.dataset.bountyElegir;
      var q = estado.buscador.q.trim();
      if (cual === 'tal-cual') {
        if (q.length >= 2) elegirCarta({ nombre: q, shopify_product_id: null, imagen: null });
      } else if (estado.buscador.resultados[Number(cual)]) {
        elegirCarta(estado.buscador.resultados[Number(cual)]);
      }
      return;
    }
    var quitar = evento.target.closest('[data-bounty-quitar]');
    if (quitar) {
      alternarQuitada(Number(quitar.dataset.bountyQuitar), true);
      return;
    }
    var regresar = evento.target.closest('[data-bounty-regresar]');
    if (regresar) {
      var indiceRegresar = Number(regresar.dataset.bountyRegresar);
      if (deshacerCambio(indiceRegresar)) refrescarRenglon(indiceRegresar, '[data-bounty-agregar]');
      else alternarQuitada(indiceRegresar, false);
      pintarNotaDisponibles();
      return;
    }
    var otra = evento.target.closest('[data-bounty-otra]');
    if (otra) {
      cambiarPorOtra(Number(otra.dataset.bountyOtra));
      return;
    }
    var chipCarta = evento.target.closest('[data-bounty-chip]');
    if (chipCarta) {
      var partes = chipCarta.dataset.bountyChip.split(':');
      var indice = Number(partes[0]);
      var clave = partes[1];
      if (!estado.acepta[indice]) return;
      estado.acepta[indice][clave] = !estado.acepta[indice][clave];
      chipCarta.setAttribute('aria-pressed', estado.acepta[indice][clave] ? 'true' : 'false');
      marcarAjustada(indice);
      return;
    }
    if (evento.target.closest('[data-bounty-siguiente]')) {
      irAPaso('cartas');
      return;
    }
    var irA = evento.target.closest('[data-bounty-ir-paso]');
    if (irA) {
      irAPaso(irA.dataset.bountyIrPaso);
      return;
    }
    if (evento.target.closest('[data-bounty-intro-seguir]')) {
      marcarIntroVista();
      estado.intro = false;
      pintar();
      enfocarInicio();
      return;
    }
    if (evento.target.closest('[data-bounty-intro-ver]')) {
      estado.intro = true;
      pintar();
      enfocarInicio();
      return;
    }
    if (evento.target.closest('[data-bounty-rebuscar]')) {
      var rebuscar = estado.rebuscar;
      cerrarModal();
      if (rebuscar) rebuscar();
      return;
    }
    if (evento.target.closest('[data-bounty-saldo-switch]')) {
      estado.saldo.usar = !estado.saldo.usar;
      estado.saldo.editando = false;
      if (estado.saldo.usar) estado.saldo.monto = null;
      pintarPie();
      var interruptor = modal.querySelector('[data-bounty-saldo-switch]');
      if (interruptor) interruptor.focus();
      return;
    }
    if (evento.target.closest('[data-bounty-saldo-cambiar]')) {
      estado.saldo.editando = true;
      pintarPie();
      var monto = modal.querySelector('input[data-bounty-saldo-monto]');
      if (monto) { monto.focus(); monto.select(); }
      return;
    }
    if (evento.target.closest('[data-bounty-saldo-listo]')) {
      guardarMontoDeSaldo();
      return;
    }
    if (evento.target.closest('[data-bounty-pedir]')) {
      // Con *Cambiar* abierto, lo escrito cuenta aunque no le haya dado Listo.
      if (estado.saldo.editando) {
        guardarMontoDeSaldo();
        return;
      }
      if (vivas(estado.cartas).length === 0) {
        if (estado.carrito.length > 0) llevarAlCarrito();
        return;
      }
      // Sin el compromiso no se paga, pero el botón no se apaga: un botón gris
      // sin explicación parece trabado. Se dice qué falta y se señala dónde.
      if (!estado.compromiso) {
        estado.faltaCompromiso = true;
        pintarPie();
        var casilla = modal.querySelector('input[data-bounty-compromiso]');
        if (casilla) casilla.focus();
        return;
      }
      enviar();
      return;
    }
    if (evento.target.closest('[data-bounty-reintentar]')) {
      enviar();
    }
  }

  /**
   * `showModal()` ya deja el resto de la página inerte y cierra con Escape. Esto
   * cubre lo que le falta: que Tab dé la vuelta dentro de la caja en vez de
   * salirse a la barra del navegador, y Escape en navegadores sin `<dialog>`.
   */
  function atraparFoco(evento) {
    if (evento.key === 'Escape' && typeof modal.showModal !== 'function') {
      cerrarModal();
      return;
    }
    if (evento.key !== 'Tab') return;
    var enfocables = Array.prototype.filter.call(
      modal.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'),
      function (nodo) { return nodo.offsetParent !== null || nodo === document.activeElement; }
    );
    if (enfocables.length === 0) return;
    var primero = enfocables[0];
    var ultimo = enfocables[enfocables.length - 1];
    if (evento.shiftKey && document.activeElement === primero) {
      evento.preventDefault();
      ultimo.focus();
    } else if (!evento.shiftKey && document.activeElement === ultimo) {
      evento.preventDefault();
      primero.focus();
    }
  }

  function alCambiar(evento) {
    // El compromiso (feedback de Mario, 16-oct): sin marcarlo no se paga.
    var compromiso = evento.target.closest('input[data-bounty-compromiso]');
    if (compromiso) {
      estado.compromiso = compromiso.checked;
      if (compromiso.checked) estado.faltaCompromiso = false;
      pintarPie();
      var otra = modal.querySelector('input[data-bounty-compromiso]');
      if (otra) otra.focus();
      return;
    }
    var radio = evento.target.closest('input[name="vault-bounty-plan"]');
    if (!radio) return;
    estado.plan = radio.value;
    actualizarCifras();
  }

  /**
   * Abre el modal.
   *   opciones.origen     'lista' | 'busqueda'
   *   opciones.cartas     [{nombre, cantidad, set?, numero?, shopify_product_id?, imagen?}]
   *   opciones.cotizacion la respuesta de `cotizar` si ya se pidió (opcional)
   *   opciones.carrito    "Completar mi mazo": las disponibles que van en el
   *                       mismo pago, [{variant_id, cantidad, nombre}] — el
   *                       nombre solo sirve para decir cuál se agotó
   *   opciones.montoHoy   lo que cuestan esas disponibles, en pesos (solo para
   *                       enseñarlo: el servidor cobra con sus propios precios)
   *   opciones.rebuscar   qué hacer si una disponible se agotó (volver a buscar)
   */
  function abrir(opciones) {
    var cartas = (opciones.cartas || []).slice(0, MAX_CARTAS);
    if (cartas.length === 0) return;

    asegurarModal();
    estado = {
      origen: opciones.origen || 'lista',
      cartasPedidas: cartas,
      recortadas: Math.max(0, (opciones.cartas || []).length - MAX_CARTAS),
      plan: PLAN_POR_DEFECTO,
      // El configurador va en dos pasos dentro de la misma caja: primero para
      // cuándo ('cuando'), luego qué acepta de cada carta ('cartas').
      paso: 'cuando',
      // Qué acepta cada carta (lo que viaja al API) y el criterio "para todas"
      // que las escribe de una vez.
      acepta: cartas.map(function () { return { edicion: false, idioma: false, lp: false }; }),
      // Las que el usuario quitó aquí (ya la tiene, ya no la quiere). Se marcan
      // en vez de sacarse para que los índices de `acepta` y `cartas` sigan
      // alineados y "Regresar" la deje como estaba.
      quitadas: cartas.map(function () { return false; }),
      // El buscador del paso 2 ("+ Agregar carta"): abierto o no, lo escrito
      // y lo que contestó la búsqueda de la tienda.
      buscador: null,
      // Las que se cambiaron por la versión que sí tenemos: índice → línea del
      // carrito que se agregó al mismo pago (para poder deshacerlo).
      cambiadas: {},
      // Las que se agregaron directo al pago desde el buscador y luego se
      // deshicieron: ya no se pintan.
      ocultas: {},
      aceptaGlobal: { edicion: false, idioma: false, lp: false },
      cartas: unirCotizacion(cartas, opciones.cotizacion),
      cotizado: Boolean(opciones.cotizacion),
      fase: opciones.cotizacion ? 'formulario' : 'cotizando',
      // La primera vez se explica qué es un Bounty antes del configurador.
      intro: !introVista(),
      error: null,
      // "Completar mi mazo": las disponibles van en el mismo pago.
      carrito: Array.isArray(opciones.carrito) ? opciones.carrito : [],
      disponibles: totalDeLineas(opciones.carrito),
      montoHoy: Math.max(0, Number(opciones.montoHoy) || 0),
      // ADR 256: "Usar mi saldo", prendido por defecto (owner, 22-oct). El
      // disponible llega con la cotización.
      saldo: {
        disponible: Math.max(0, Number(opciones.cotizacion && opciones.cotizacion.saldo_disponible) || 0),
        usar: true,
        monto: null,
        editando: false
      },
      rebuscar: typeof opciones.rebuscar === 'function' ? opciones.rebuscar : null,
      agotadas: [],
      devolverFoco: document.activeElement
    };

    pintar();
    if (typeof modal.showModal === 'function') {
      modal.showModal();
    } else {
      modal.setAttribute('open', '');
    }
    document.documentElement.style.overflow = 'hidden';
    enfocarInicio();

    if (!opciones.cotizacion) {
      var deEsteModal = estado;
      cotizar(cuerpoDeSolicitud(estado.origen, PLAN_POR_DEFECTO, cartas)).then(function (r) {
        if (estado !== deEsteModal) return;
        // Si `cotizar` no contesta, el modal sigue: las cartas quedan con
        // "precio por confirmar" y el operador lo confirma. Que el cotizador
        // falle no puede impedir pedirlas.
        if (r.ok && r.datos) {
          estado.cartas = unirCotizacion(cartas, r.datos);
          estado.cotizado = true;
          estado.saldo.disponible = Math.max(0, Number(r.datos.saldo_disponible) || 0);
        }
        // Piloto: fuera de sus regiones se dice desde el principio, no al pagar.
        if (r.status === 403 && r.datos && r.datos.error === 'fuera_del_piloto') {
          estado.error = mensajeDeError(r.status, r.datos);
        }
        estado.fase = 'formulario';
        if (estado.intro) return; // se pinta al pasar al configurador
        pintar();
        enfocarInicio();
      });
    }
  }

  function enfocarInicio() {
    var destino = modal.querySelector('[data-bounty-foco-inicial]') || modal.querySelector('[data-bounty-cerrar]');
    if (destino) destino.focus();
  }

  function totalDeLineas(carrito) {
    return (carrito || []).reduce(function (suma, linea) {
      return suma + Math.max(1, Math.round(Number(linea && linea.cantidad) || 1));
    }, 0);
  }

  /** Los nombres de las variantes que el servidor dijo agotadas, sin repetir. */
  function nombresAgotados(carrito, variantes) {
    var ids = (variantes || []).map(function (v) { return idNumerico(v && typeof v === 'object' ? v.variant_id : v); });
    var nombres = [];
    (carrito || []).forEach(function (linea) {
      var nombre = String(linea.nombre || '');
      if (nombre && ids.indexOf(idNumerico(linea.variant_id)) !== -1 && nombres.indexOf(nombre) === -1) nombres.push(nombre);
    });
    return nombres;
  }

  function enviar() {
    if (!estado || estado.fase === 'enviando') return;
    estado.fase = 'enviando';
    estado.error = null;
    pintarPie();

    var deEsteModal = estado;
    var cuerpo = cuerpoDeCheckout(estado.origen, estado.plan, vivas(estado.cartasPedidas), vivas(estado.acepta), estado.carrito, estado.saldo);
    pagar(cuerpo).then(function (r) {
      if (estado !== deEsteModal) return;
      if (r.status === 201 || (r.ok && r.datos && r.datos.codigo)) {
        estado.creado = r.datos || {};
        if (estado.creado.pagado) {
          // ADR 256: el Saldo cubrió todo. No hay checkout: ya está pagado.
          estado.fase = 'pagado';
        } else if (cuerpo.saldo && estado.creado.saldo_no_aplicado && estado.creado.invoice_url) {
          // Pidió Saldo y no se pudo usar: no se le cobra todo con tarjeta
          // sin avisarle. El siguiente clic (ya sin Saldo) reusa el mismo pago.
          estado.saldo.usar = false;
          estado.fase = 'formulario';
          estado.error = estado.creado.saldo_no_aplicado === 'saldo_insuficiente'
            ? 'Tu saldo cambió y ya no alcanza. Revisa el total y vuelve a pagar.'
            : 'Esta vez no pudimos usar tu saldo. Tu saldo no se tocó: puedes pagar con tarjeta.';
          pintarPie();
          enfocarError();
          return;
        } else if (!estado.creado.invoice_url) {
          // No había nada que cobrar hoy (todas sin precio y sin disponibles).
          estado.fase = 'creado';
        } else if (!urlDePago(estado.creado.invoice_url)) {
          estado.fase = 'formulario';
          estado.error = 'Tu Bounty ' + (estado.creado.codigo || '') + ' quedó guardado, pero el link de pago no es de The Vault y no te mandamos ahí. Escríbenos por WhatsApp con ese código.';
          pintar();
          enfocarError();
          return;
        } else if (modoSimulado()) {
          estado.fase = 'simulado';
        } else {
          estado.fase = 'redirigiendo';
          pintar();
          window.location.assign(urlDePago(estado.creado.invoice_url));
          return;
        }
        pintar();
        enfocarInicio();
        return;
      }
      if (r.status === 401) {
        estado.fase = 'sin_sesion';
        pintar();
        enfocarInicio();
        return;
      }
      if (r.status === 409 && r.datos && r.datos.error === 'sin_stock') {
        estado.fase = 'sin_stock';
        estado.agotadas = nombresAgotados(estado.carrito, r.datos.variantes);
        pintar();
        enfocarInicio();
        return;
      }
      estado.fase = 'formulario';
      estado.error = mensajeDeError(r.status, r.datos);
      pintarPie();
      enfocarError();
    });
  }

  function enfocarError() {
    var alerta = modal.querySelector('.vault-bounty__error');
    if (alerta) alerta.focus();
  }

  /* ---- Pintado ---- */

  /** `insignia` es un chip ya armado (o nada). */
  function htmlEncabezado(titulo, subtitulo, insignia, extra) {
    return '' +
      '<header class="vault-bounty__encabezado">' +
        '<div class="vault-bounty__encabezado-texto">' +
          '<div class="vault-bounty__titulo-fila">' +
            '<h2 class="vault-bounty__titulo" id="vault-bounty-titulo">' + esc(titulo) + '</h2>' +
            (insignia || '') +
          '</div>' +
          (subtitulo ? '<p class="vault-bounty__subtitulo">' + esc(subtitulo) + '</p>' : '') +
          (extra || '') +
        '</div>' +
        '<button type="button" class="vault-bounty__cerrar" data-bounty-cerrar aria-label="Cerrar">' +
          icono('cerrar', 18) +
        '</button>' +
      '</header>';
  }

  function pintar() {
    if (!modal || !estado) return;
    var html;
    if (estado.intro && (estado.fase === 'cotizando' || estado.fase === 'formulario')) {
      modal.innerHTML = '<div class="vault-bounty__caja">' + htmlIntro() + '</div>';
      return;
    }
    if (estado.fase === 'creado') html = htmlCreado();
    else if (estado.fase === 'simulado') html = htmlSimulado();
    else if (estado.fase === 'redirigiendo') html = htmlRedirigiendo();
    else if (estado.fase === 'pagado') html = htmlPagado();
    else if (estado.fase === 'sin_stock') html = htmlSinStock();
    else if (estado.fase === 'sin_sesion') html = htmlSinSesion();
    else html = htmlFormulario();
    // Caja nueva en cada fase: su animación de entrada (fade + slide corto)
    // corre sola, y `prefers-reduced-motion` la apaga en el CSS.
    modal.innerHTML = '<div class="vault-bounty__caja">' + html + '</div>';
    if (estado.fase === 'formulario' || estado.fase === 'enviando' || estado.fase === 'cotizando') {
      actualizarCifras();
      pintarPie();
    }
  }

  var CLAVE_INTRO = 'vault_bounty_intro_v1';

  function introVista() {
    try { return window.localStorage.getItem(CLAVE_INTRO) === '1'; } catch (e) { return false; }
  }

  function marcarIntroVista() {
    try { window.localStorage.setItem(CLAVE_INTRO, '1'); } catch (e) { /* sin almacenamiento: se vuelve a mostrar */ }
  }

  /**
   * Primera vez: qué es un Bounty, cómo funciona y qué cuesta, antes de pedir
   * cualquier decisión. Un solo CTA: configurar.
   */
  function htmlIntro() {
    var copias = totalDeCopias(estado.cartasPedidas);
    var visibles = estado.cartasPedidas.slice(0, 5);
    var abanico = visibles.map(function (carta, i) {
      return '<span class="vb-intro__carta" style="--i:' + i + ';--n:' + visibles.length + '">' +
        '<img src="' + esc(imagenDe(carta)) + '" alt="" decoding="async"></span>';
    }).join('');
    var lo = copias === 1 ? 'la' : 'las';

    return htmlEncabezado('Conoce Bounty', null, chip('info', 'estrella', 'Nuevo')) +
      '<div class="vault-bounty__cuerpo vb-intro">' +
        '<div class="vb-intro__heroe">' +
          '<div class="vb-intro__abanico" aria-hidden="true">' + abanico + '</div>' +
          '<div class="vb-intro__texto">' +
            '<p class="vb-intro__titular">¿No tenemos ' + (copias === 1 ? 'esa carta' : 'todas tus cartas') + '? Te ' + lo + ' conseguimos.</p>' +
            '<p class="vb-intro__definicion">Un <strong>Bounty</strong> es un pedido de cartas que hoy no están en la bóveda. Tu operador regional ' + lo + ' busca con su red de dealers y te avisamos en cuanto ' + lo + ' tenga.</p>' +
          '</div>' +
        '</div>' +
        '<ol class="vb-intro__pasos" aria-label="Cómo funciona">' +
          '<li class="vb-intro__paso">' +
            '<span class="vb-pasos__num" aria-hidden="true">1</span>' +
            '<strong>Haces tu pedido</strong>' +
            '<span>Eliges para cuándo ' + lo + ' quieres (🐢 30 días, 🚀 20 días o ⚡ 15 días) y qué versiones aceptas.</span>' +
          '</li>' +
          '<li class="vb-intro__paso">' +
            '<span class="vb-pasos__num" aria-hidden="true">2</span>' +
            '<strong>Tu operador ' + lo + ' busca</strong>' +
            '<span>' + (copias === 1 ? 'La consigue' : 'Las consigue') + ' con dealers de tu región y revisa cada carta antes de aceptarla.</span>' +
          '</li>' +
          '<li class="vb-intro__paso">' +
            '<span class="vb-pasos__num" aria-hidden="true">3</span>' +
            '<strong>Apartas hoy, recuperas lo que sobre</strong>' +
            '<span>Pagas el tope de cada carta. Si sale más barata o no aparece, la diferencia regresa a tu Saldo.</span>' +
          '</li>' +
        '</ol>' +
        '<div class="vb-intro__garantias">' +
          chip('success', 'check', 'Pagas un solo checkout') +
          chip('success', 'monedas', 'Lo que sobre regresa a tu Saldo') +
          chip('info', 'boveda', 'Revisada por tu operador') +
        '</div>' +
      '</div>' +
      '<footer class="vault-bounty__pie">' +
        '<button type="button" class="vb-cta vault-bounty__primario" data-bounty-intro-seguir data-bounty-foco-inicial>' +
          '<span>Configurar mi Bounty</span>' + icono('flecha', 16) +
        '</button>' +
        '<button type="button" class="vb-secundario vb-intro__ahora-no" data-bounty-cerrar>Ahora no</button>' +
      '</footer>';
  }

  function htmlFormulario() {
    var copias = totalDeCopias(vivas(estado.cartasPedidas));
    // La explicación vive en la intro (primera vez) y en "¿Cómo funciona?".
    // Aquí el encabezado solo dice qué se decide.
    var subtitulo = 'Elige para cuándo ' + (copias === 1 ? 'la quieres' : 'las quieres') + ' y qué versiones aceptas.';
    var insignia = chip('pending', 'buscar', '<span data-bounty-por-conseguir>' + copias + ' por conseguir</span>') +
      '<button type="button" class="vb-enlace-intro" data-bounty-intro-ver>' + icono('info', 13) + '<span>¿Cómo funciona?</span></button>';
    var extra = htmlNotaDisponibles();

    if (estado.fase === 'cotizando') {
      return htmlEncabezado('Pon un Bounty', subtitulo, insignia, extra) +
        '<div class="vault-bounty__cuerpo">' +
          aviso('neutral', 'cargando', 'Calculando lo que pagarías por cada carta…', ' role="status" data-cargando') +
        '</div>' +
        '<footer class="vault-bounty__pie" data-bounty-pie></footer>';
    }

    var cuerpo = estado.paso === 'cartas' ? htmlPasoCartas() : htmlPasoCuando();
    return htmlEncabezado('Pon un Bounty', subtitulo, insignia, extra) +
      '<div class="vault-bounty__cuerpo">' +
        htmlIndicadorDePasos() +
        (estado.cotizado ? '' : aviso('pending', 'alerta', 'No pudimos calcular los precios ahora. Puedes seguir: el total exacto lo ves en la página de pago, antes de confirmar.', ' role="status"')) +
        cuerpo +
      '</div>' +
      '<footer class="vault-bounty__pie" data-bounty-pie></footer>';
  }

  var PASOS = [
    { clave: 'cuando', nombre: 'Para cuándo' },
    { clave: 'cartas', nombre: 'Las cartas' }
  ];

  /** "1 Para cuándo → 2 Las cartas". Un paso ya hecho es botón: regresa a él. */
  function htmlIndicadorDePasos() {
    var actual = estado.paso === 'cartas' ? 1 : 0;
    return '<ol class="vb-indicador" aria-label="Pasos">' +
      PASOS.map(function (paso, i) {
        var hecho = i < actual;
        var marca = '<span class="vb-indicador__num" aria-hidden="true">' + (hecho ? icono('check', 12) : String(i + 1)) + '</span>';
        // Ya hecho, el paso 1 dice el plan elegido: es lo único que hay que
        // recordar del paso anterior, y es botón para cambiarlo.
        var plan = planDe(estado.plan);
        var etiqueta = hecho && paso.clave === 'cuando'
          ? plan.emoji + ' ' + esc(plan.nombre) + ' · ' + plan.dias + ' días'
          : esc(paso.nombre);
        var contenido = hecho
          ? '<button type="button" class="vb-indicador__boton" data-bounty-ir-paso="' + paso.clave + '" aria-label="Cambiar para cuándo: ' + esc(plan.nombre) + ', ' + plan.dias + ' días">' + marca + '<span>' + etiqueta + '</span></button>'
          : '<span class="vb-indicador__boton">' + marca + '<span>' + esc(paso.nombre) + '</span></span>';
        return '<li class="vb-indicador__paso' + (hecho ? ' is-hecho' : '') + (i === actual ? ' is-actual' : '') + '"' +
          (i === actual ? ' aria-current="step"' : '') + '>' + contenido + '</li>';
      }).join('<li class="vb-indicador__linea" aria-hidden="true"></li>') +
    '</ol>';
  }

  function htmlPasoCuando() {
    return '' +
      '<fieldset class="vb-planes">' +
        '<legend class="vb-seccion" id="vb-paso-titulo" tabindex="-1">¿Para cuándo ' + (totalDeCopias(vivas(estado.cartasPedidas)) === 1 ? 'la quieres' : 'las quieres') + '?</legend>' +
        '<div class="vb-planes__lista">' + PLANES.map(htmlPlan).join('') + '</div>' +
        '<p class="vb-nota">La recompensa es lo que gana el operador por conseguirla. El tope es lo más que puede subir el precio de la carta sobre el de referencia.</p>' +
      '</fieldset>';
  }

  function htmlPasoCartas() {
    if (estado.buscador) return htmlBuscador();
    return '' +
      '<section class="vb-bloque" aria-labelledby="vb-paso-titulo">' +
        '<h3 class="vb-seccion" id="vb-paso-titulo" tabindex="-1">Qué aceptas</h3>' +
        '<div class="vb-acepta" role="group" aria-label="Qué aceptas, para todas las cartas">' +
          '<span class="vb-acepta__etiqueta">Para todas:</span>' +
          CHIPS.map(function (c) {
            return '<button type="button" class="vb-toggle" data-bounty-global="' + c.clave + '" aria-pressed="' +
              (estado.aceptaGlobal[c.clave] ? 'true' : 'false') + '">' + icono('check', 13) + '<span>' + esc(c.texto) + '</span></button>';
          }).join('') +
        '</div>' +
      '</section>' +
      '<section class="vb-bloque" aria-labelledby="vb-cartas-titulo">' +
        '<div class="vb-cartas__cabeza">' +
          '<h3 class="vb-seccion" id="vb-cartas-titulo">Las cartas <span class="vb-seccion__cuenta" data-bounty-cuenta>' + vivas(estado.cartas).length + '</span></h3>' +
          (estado.cartas.length < MAX_CARTAS
            ? '<button type="button" class="vb-carta__ajustar vb-agregar" data-bounty-agregar>' + icono('mas', 16) + '<span>Agregar carta</span></button>'
            : '') +
        '</div>' +
        (estado.recortadas > 0
          ? aviso('info', 'info', 'Un Bounty lleva hasta ' + MAX_CARTAS + ' cartas distintas; las otras ' + estado.recortadas + ' quedan para otro.')
          : '') +
        '<ul class="vb-cartas">' + estado.cartas.map(htmlCarta).join('') + '</ul>' +
      '</section>' +
      '<div class="vb-avisos" data-bounty-resumen aria-live="polite"></div>';
  }

  function irAPaso(paso) {
    if (!estado || (paso !== 'cuando' && paso !== 'cartas')) return;
    estado.paso = paso;
    pintar();
    var titulo = modal.querySelector('#vb-paso-titulo');
    if (titulo) titulo.focus();
    var cuerpo = modal.querySelector('.vault-bounty__cuerpo');
    if (cuerpo) cuerpo.scrollTop = 0;
  }

  function htmlPlan(plan) {
    var marcado = plan.clave === estado.plan;
    return '' +
      '<label class="vb-plan">' +
        '<input type="radio" name="vault-bounty-plan" value="' + plan.clave + '"' +
          (marcado ? ' checked data-bounty-foco-inicial' : '') + '>' +
        '<span class="vb-plan__caja">' +
          '<span class="vb-plan__cabeza">' +
            '<span class="vb-plan__marca" aria-hidden="true"></span>' +
            '<span class="vb-plan__nombre">' + (plan.emoji ? '<span class="vb-plan__emoji" aria-hidden="true">' + plan.emoji + '</span>' : '') + esc(plan.nombre) + '</span>' +
            (plan.destacado ? chip('info', 'estrella', esc(plan.destacado)) : '') +
          '</span>' +
          '<span class="vb-plan__chips">' +
            chip('neutral', 'calendario', plan.dias + ' días') +
            chip('neutral', 'monedas', 'Recompensa ' + Math.round(plan.recompensa * 100) + '%') +
            chip('neutral', 'sube', 'Tope +' + Math.round(plan.tope * 100) + '%') +
          '</span>' +
          '<span class="vb-plan__rango" data-bounty-rango-plan="' + plan.clave + '"></span>' +
        '</span>' +
      '</label>';
  }

  function htmlCarta(carta, i) {
    if (estado.ocultas[i]) return '';
    if (estado.quitadas[i]) {
      return '' +
        '<li class="vb-carta vb-carta--quitada" data-bounty-renglon="' + i + '">' +
          '<div class="vb-carta__fila">' +
            '<img class="vb-carta__imagen" src="' + esc(imagenDe(carta)) + '" alt="' + esc(carta.nombre) + '" decoding="async" loading="lazy">' +
            '<div class="vb-carta__datos">' +
              '<p class="vb-carta__nombre">' + esc(carta.nombre) + '</p>' +
              '<div class="vb-carta__estados">' +
                (estado.cambiadas[i]
                  ? chip('success', 'check',
                      (estado.cambiadas[i].directa
                        ? 'La tenemos · va en tu pago de hoy' + (estado.cambiadas[i].cantidad > 1 ? ' · ' + estado.cambiadas[i].cantidad + ' copias' : '')
                        : 'Te llevas la que tenemos') +
                      ' · ' + esc(carta.otra_version.titulo))
                  : chip('neutral', 'quitar', 'Quitada del Bounty')) +
              '</div>' +
            '</div>' +
            '<button type="button" class="vb-carta__ajustar" data-bounty-regresar="' + i + '" aria-label="' +
              (estado.cambiadas[i] ? 'Deshacer el cambio de ' : 'Regresar ') + esc(carta.nombre) + (estado.cambiadas[i] ? '' : ' al Bounty') + '">' +
              icono('regresar', 14) + '<span>' + (estado.cambiadas[i] ? 'Deshacer' : 'Regresar') + '</span>' +
            '</button>' +
          '</div>' +
        '</li>';
    }

    var precio = carta.precio_base !== null
      ? '<span class="vb-carta__precio">' + esc(pesos(carta.precio_base)) + '</span>' +
        '<span class="vb-carta__tope" data-bounty-tope="' + i + '"></span>'
      : chip('neutral', 'etiqueta', 'Precio por confirmar');

    var estados = [
      carta.ya_la_tienes ? chip('warning', 'boveda', 'Ya tienes una en tu bóveda') : '',
      htmlOtraVersion(carta.otra_version, ' data-bounty-otra="' + i + '"'),
      chip('neutral', null, 'Ajustada', ' data-bounty-ajustada="' + i + '" hidden')
    ].join('');

    return '' +
      '<li class="vb-carta" data-bounty-renglon="' + i + '">' +
        '<div class="vb-carta__fila">' +
          '<img class="vb-carta__imagen" src="' + esc(imagenDe(carta)) + '" alt="' + esc(carta.nombre) + '" decoding="async" loading="lazy">' +
          '<div class="vb-carta__datos">' +
            '<p class="vb-carta__nombre">' + esc(carta.nombre) + '</p>' +
            '<p class="vb-carta__version">' + esc(versionDe(carta)) + '</p>' +
            '<div class="vb-carta__estados">' + estados + '</div>' +
            '<div class="vb-cantidad" role="group" aria-label="Copias de ' + esc(carta.nombre) + '">' +
              '<button type="button" class="vb-cantidad__paso" data-bounty-cantidad="' + i + ':-1" aria-label="Una ' + esc(carta.nombre) + ' menos"' + (carta.cantidad <= 1 ? ' disabled' : '') + '>' + icono('menos', 14) + '</button>' +
              '<span class="vb-cantidad__valor" aria-live="polite">' + esc(carta.cantidad) + '</span>' +
              '<button type="button" class="vb-cantidad__paso" data-bounty-cantidad="' + i + ':1" aria-label="Una ' + esc(carta.nombre) + ' más"' + (carta.cantidad >= MAX_CANTIDAD ? ' disabled' : '') + '>' + icono('mas', 14) + '</button>' +
            '</div>' +
          '</div>' +
          '<div class="vb-carta__precios">' + precio + '</div>' +
          '<button type="button" class="vb-carta__ajustar" data-bounty-ajustar="' + i + '" aria-expanded="false" aria-controls="vb-ajuste-' + i + '">' +
            '<span>Ajustar</span>' + icono('abajo', 14) +
          '</button>' +
          '<button type="button" class="vb-carta__ajustar vb-carta__quitar" data-bounty-quitar="' + i + '" aria-label="Quitar ' + esc(carta.nombre) + ' del Bounty" title="Quitar del Bounty">' +
            icono('quitar', 16) +
          '</button>' +
        '</div>' +
        '<div class="vb-carta__ajuste" id="vb-ajuste-' + i + '" role="group" aria-label="Qué aceptas solo para ' + esc(carta.nombre) + '" hidden>' +
          '<span class="vb-acepta__etiqueta">Solo para esta:</span>' +
          CHIPS.map(function (c) {
            var activo = estado.acepta[i] && estado.acepta[i][c.clave];
            return '<button type="button" class="vb-toggle" data-bounty-chip="' + i + ':' + c.clave + '" aria-pressed="' + (activo ? 'true' : 'false') + '">' +
              icono('check', 13) + '<span>' + esc(c.texto) + '</span></button>';
          }).join('') +
        '</div>' +
      '</li>';
  }

  /** Lo que cambia con el plan, sin repintar el modal (no se pierde el scroll ni el foco). */
  function actualizarCifras() {
    if (!modal || !estado) return;

    PLANES.forEach(function (plan) {
      var nodo = modal.querySelector('[data-bounty-rango-plan="' + plan.clave + '"]');
      if (nodo) nodo.textContent = textoDeRango(rangoTotal(vivas(estado.cartas), plan));
    });

    var plan = planDe(estado.plan);
    estado.cartas.forEach(function (carta, i) {
      var nodo = modal.querySelector('[data-bounty-tope="' + i + '"]');
      if (!nodo) return;
      var tope = carta.precio_base * (1 + plan.tope);
      nodo.textContent = 'tope ' + pesos(tope);
    });

    pintarPie();

    var resumen = modal.querySelector('[data-bounty-resumen]');
    if (!resumen) return;
    var total = rangoTotal(vivas(estado.cartas), plan);
    resumen.innerHTML = '' +
      (vivas(estado.cartas).length === 0
        ? aviso('neutral', 'quitar', 'Quitaste todas las cartas. Regresa al menos una para pedir el Bounty, o cierra para llevarte solo lo que hay.')
        : '') +
      (total.conPrecio > 0
        ? aviso('info', 'info', '<strong>Apartas el tope de cada carta (' + esc(pesos(total.max)) + ').</strong> Si sale más barata o no aparece, la diferencia regresa a tu Saldo.')
        : '') +
      (total.porConfirmar > 0
        ? aviso('neutral', 'etiqueta', total.porConfirmar + (total.porConfirmar === 1
            ? ' carta no tiene precio todavía: no se cobra hoy; te mandamos su link de pago cuando tu operador la consiga.'
            : ' cartas no tienen precio todavía: no se cobran hoy; te mandamos su link de pago cuando tu operador las consiga.'))
        : '');
  }

  function textoDeRango(total) {
    if (total.conPrecio === 0) return 'Precio por confirmar';
    return 'Entre ' + pesos(total.min) + ' y ' + pesos(total.max);
  }

  /**
   * El texto del CTA y el desglose del pago, con el plan elegido. Puro: lo
   * prueba `docs/pruebas/vault-bounty.nucleo.mjs`.
   *   "32 cartas hoy $30,753 + Bounty de 8 (apartado) $4,450 = $35,203"
   */
  function resumenDePago(montoHoy, disponibles, cartas, plan, saldo) {
    var pago = totalAPagar(montoHoy, cartas, plan);
    var hay = Math.max(0, Math.round(Number(disponibles) || 0));
    var partes = [];
    if (hay > 0) partes.push((hay === 1 ? '1 carta hoy ' : hay + ' cartas hoy ') + pesos(pago.hoy));
    if (pago.apartado > 0) partes.push('Bounty de ' + pago.copiasConPrecio + ' (apartado) ' + pesos(pago.apartado));
    var desglose = partes.join(' + ') + (partes.length > 1 ? ' = ' + pesos(pago.total) : '');
    // ADR 256: el Saldo se resta en el mismo renglón y el total que resalta es
    // lo que de verdad sale de la tarjeta.
    var usado = saldoQueUsa(saldo, pago.total);
    var falta = Math.max(0, Math.round((pago.total - usado) * 100) / 100);
    if (usado > 0) desglose = (partes.length > 1 ? pesos(pago.total) : partes.join('')) + ' − tu saldo ' + pesos(usado) + ' = ' + pesos(falta);
    var cta;
    if (usado > 0 && falta < 0.5) cta = 'Pagar con mi saldo';
    else if (hay > 0) cta = 'Pagar todo · ' + pesos(falta);
    else if (pago.apartado > 0) cta = 'Apartar ' + pesos(falta) + ' y pedir';
    else cta = null; // nada que cobrar hoy
    return { pago: pago, desglose: desglose, cta: cta, saldo: usado, falta: falta };
  }

  /**
   * ADR 256: cuánto Saldo se usa en este pago. Por defecto todo el que
   * alcance; con *Cambiar*, ese monto. Nunca más que el disponible ni que el
   * total. Puro.
   */
  function saldoQueUsa(saldo, total) {
    if (!saldo || !saldo.usar) return 0;
    var disponible = Math.max(0, Number(saldo.disponible) || 0);
    var pedido = saldo.monto == null ? disponible : Math.max(0, Number(saldo.monto) || 0);
    return Math.round(Math.min(pedido, disponible, Math.max(0, Number(total) || 0)) * 100) / 100;
  }

  /** El total ("= $35,203") resalta; el resto del desglose va en texto normal. */
  function htmlDesglose(texto) {
    var separador = texto.lastIndexOf(' = ') !== -1 ? ' = ' : ' ';
    var corte = texto.lastIndexOf(separador);
    if (corte === -1 || texto.indexOf('$') === -1) return esc(texto);
    return esc(texto.slice(0, corte)) + separador + '<strong>' + esc(texto.slice(corte + separador.length)) + '</strong>';
  }

  function pintarPie() {
    var pie = modal && modal.querySelector('[data-bounty-pie]');
    if (!pie) return;
    var copias = totalDeCopias(vivas(estado.cartasPedidas));
    var enviando = estado.fase === 'enviando';
    var cotizando = estado.fase === 'cotizando';
    var resumen = resumenDePago(estado.montoHoy, estado.disponibles, vivas(estado.cartas), planDe(estado.plan), estado.saldo);

    // Paso 1: todavía no se paga; el botón lleva al paso 2 y el desglose ya
    // enseña lo que costaría con el plan elegido.
    if (estado.paso === 'cuando' && !cotizando) {
      pie.innerHTML = '' +
        '<div class="vb-pie">' +
          '<div class="vb-pie__total" aria-live="polite">' +
            '<span class="vb-pie__etiqueta">Paso 1 de 2 · con este plan pagarías</span>' +
            '<span class="vb-pie__desglose">' + htmlDesglose(resumen.desglose || 'Hoy no se cobra nada') + '</span>' +
          '</div>' +
          '<button type="button" class="vb-cta vault-bounty__primario vb-pie__cta" data-bounty-siguiente>' +
            '<span>Continuar</span>' + icono('flecha', 18) +
          '</button>' +
        '</div>';
      return;
    }

    var sinCartas = copias === 0;
    // Todo se cambió por versiones que sí tenemos: ya no hay Bounty, es una compra normal.
    var soloCarrito = sinCartas && estado.carrito.length > 0;
    var bloqueado = enviando || cotizando || (sinCartas && !soloCarrito);
    var texto = enviando
      ? 'Armando tu pago…'
      : soloCarrito
        ? 'Llevar al carrito · ' + pesos(estado.montoHoy)
        : sinCartas
        ? 'Sin cartas que pedir'
        : resumen.cta || (copias === 1 ? 'Pedir esta carta' : 'Pedir estas ' + copias + ' cartas');
    var desglose = cotizando
      ? 'Calculando…'
      : resumen.desglose || 'Hoy no se cobra nada';

    pie.innerHTML = '' +
      (estado.error
        ? aviso('danger', 'error', esc(estado.error), ' role="alert" tabindex="-1"', 'vault-bounty__error')
        : '') +
      // El compromiso, antes de pagar: qué hacemos, a dónde regresa lo que
      // sobre y cuánto cuesta quitar una carta (feedback de Mario, 16-oct).
      '<label class="vb-compromiso' + (estado.faltaCompromiso ? ' vb-compromiso--falta' : '') + '">' +
        '<input type="checkbox" data-bounty-compromiso' + (estado.compromiso ? ' checked' : '') + '>' +
        '<span>Entiendo: buscamos mis cartas en el plazo de mi plan. Lo que no se consiga regresa a mi Saldo, no a mi tarjeta. ' +
        'Puedo quitar una carta gratis en la primera hora después de pagar; después, su recompensa no regresa.</span>' +
      '</label>' +
      (estado.faltaCompromiso
        ? '<p class="vb-compromiso__falta" role="alert">Marca la casilla para continuar.</p>'
        : '') +
      (soloCarrito ? '' : htmlSaldo(resumen)) +
      // El desglose viaja junto al botón: quien decide ve qué paga y por qué
      // sin regresar a buscarlo.
      '<div class="vb-pie">' +
        '<div class="vb-pie__total" aria-live="polite">' +
          '<span class="vb-pie__etiqueta">' + (estado.disponibles > 0 ? 'Un solo pago' : resumen.cta ? 'Apartas hoy' : 'Pagarías') + '</span>' +
          '<span class="vb-pie__desglose" data-bounty-pie-desglose>' + htmlDesglose(desglose) + '</span>' +
          (resumen.pago.apartado > 0
            ? '<span class="vb-pie__hoy">' + icono('monedas', 12) + '<span>Lo que no se consiga, o lo que salga más barato, regresa a tu Saldo.</span></span>'
            : '') +
        '</div>' +
        '<button type="button" class="vb-cta vault-bounty__primario vb-pie__cta" data-bounty-pedir' +
          (bloqueado ? ' disabled aria-disabled="true"' : '') + '>' +
          (enviando ? icono('cargando', 18) : '') + '<span>' + esc(texto) + '</span>' +
          (enviando || cotizando ? '' : icono('flecha', 18)) +
        '</button>' +
      '</div>';
  }

  /**
   * ADR 256 §1 — "Usar mi saldo", el mismo bloque que el carrito (ADR 241):
   * interruptor verde, cuánto usas y *Cambiar*. Solo si hay Saldo y algo que
   * pagar hoy.
   */
  function htmlSaldo(resumen) {
    var saldo = estado.saldo;
    if (!saldo || !(saldo.disponible > 0) || !(resumen.pago.total > 0)) return '';
    var usando = saldo.usar && resumen.saldo > 0;
    return '<div class="rs__saldo vb-saldo" data-bounty-saldo>' +
      '<div class="rs__row">' +
        '<div>' +
          '<p class="rs__t">Usar mi saldo</p>' +
          (usando
            ? '<p class="rs__s">Usas <span class="rs__pill rs__pill--saldo">' + esc(pesos(resumen.saldo)) + '</span>' +
              ' de ' + esc(pesos(saldo.disponible)) + ' · <button type="button" class="rs__link" data-bounty-saldo-cambiar>Cambiar</button></p>'
            : '<p class="rs__s">Tienes <span class="rs__pill rs__pill--saldo">' + esc(pesos(saldo.disponible)) + '</span></p>') +
        '</div>' +
        '<button type="button" class="rs__switch" role="switch" aria-checked="' + (saldo.usar ? 'true' : 'false') + '"' +
          ' aria-label="Usar mi saldo" data-bounty-saldo-switch></button>' +
      '</div>' +
      (saldo.editando
        ? '<div class="rs__monto">' +
            '<label class="rs__s" for="vb-saldo-monto">Cuánto</label>' +
            '<input id="vb-saldo-monto" type="number" inputmode="decimal" min="1" step="1" max="' +
              esc(String(Math.floor(Math.min(saldo.disponible, resumen.pago.total)))) + '" value="' +
              esc(String(Math.round(resumen.saldo))) + '" data-bounty-saldo-monto>' +
            '<button type="button" class="rs__link" data-bounty-saldo-listo>Listo</button>' +
          '</div>'
        : '') +
    '</div>';
  }

  /** El monto de *Cambiar*: vacío o 0 apaga; más del disponible se topa. */
  function guardarMontoDeSaldo() {
    var campo = modal && modal.querySelector('input[data-bounty-saldo-monto]');
    if (campo) {
      var valor = parseFloat(campo.value);
      if (!(valor > 0)) {
        estado.saldo.usar = false;
        estado.saldo.monto = null;
      } else {
        estado.saldo.monto = Math.min(valor, estado.saldo.disponible);
      }
    }
    estado.saldo.editando = false;
    pintarPie();
  }

  /**
   * Sin sesión: crear cuenta o entrar. Las dos abren CardVault en otra pestaña
   * y la lista se queda aquí; al volver a esta pestaña se reintenta solo
   * (`alVolver`), sin un tercer botón. Un comprador nuevo elige su estado en
   * el onboarding; por el piloto, tiene que ser CDMX.
   */
  function htmlSinSesion() {
    var base = apiBase();
    return htmlEncabezado('Crea tu cuenta para pagar', null, null) +
      '<div class="vault-bounty__cuerpo">' +
        aviso('info', 'entrar',
          '<strong>Tu pago y tu Bounty quedan a tu nombre.</strong> Así lo que sobre regresa a tu Saldo y te avisamos cuando tu operador consiga las cartas. ' +
          'Se abre en otra pestaña y tu lista se queda aquí. Al crear tu cuenta, elige tu estado: por ahora Bounty funciona en CDMX. ' +
          'Cuando vuelvas a esta pestaña, seguimos.') +
      '</div>' +
      '<footer class="vault-bounty__pie">' +
        '<a class="vb-cta vault-bounty__primario" href="' + esc(base + '/signup') + '" target="_blank" rel="noopener" data-bounty-foco-inicial>' +
          icono('entrar', 18) + '<span>Crear cuenta</span></a>' +
        '<a class="vb-secundario vault-bounty__secundario" href="' + esc(base + '/login') + '" target="_blank" rel="noopener">Ya tengo cuenta</a>' +
      '</footer>';
  }

  /** De regreso de crear la cuenta o entrar (otra pestaña): se reintenta el pago. */
  function alVolver() {
    if (document.visibilityState !== 'visible') return;
    if (!estado || estado.fase !== 'sin_sesion') return;
    enviar();
  }
  // Las pruebas del núcleo corren en Node, sin `document`.
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', alVolver);

  /** Sin nada que cobrar hoy: todas sin precio y sin disponibles. */
  function htmlCreado() {
    var creado = estado.creado || {};
    var url = urlSegura(creado.url_seguimiento);
    var vence = creado.vence_at ? fechaMx(creado.vence_at) : fechaMx(venceAt(estado.plan));
    var copias = totalDeCopias(vivas(estado.cartasPedidas));
    return htmlEncabezado('Tu Bounty está puesto', null, null) +
      '<div class="vault-bounty__cuerpo">' +
        htmlRecibo(chip('success', 'check', 'Bounty puesto'), creado.codigo,
          chip('neutral', 'reloj', 'Vence el ' + esc(vence)) +
          chip('pending', 'buscar', copias + (copias === 1 ? ' carta por conseguir' : ' cartas por conseguir'))) +
        aviso('info', 'info', '<strong>Hoy no se cobró nada:</strong> ninguna tiene precio todavía. Te mandamos el link de pago de cada carta cuando tu operador la consiga.') +
      '</div>' +
      '<footer class="vault-bounty__pie">' +
        (url
          ? '<a class="vb-cta vault-bounty__primario" href="' + esc(url) + '" data-bounty-foco-inicial>Ver mi Bounty</a>'
          : '<button type="button" class="vb-cta vault-bounty__primario" data-bounty-cerrar data-bounty-foco-inicial>Listo</button>') +
      '</footer>';
  }

  /** `insignia` y `chips` ya armados; el código se escapa aquí. */
  function htmlRecibo(insignia, codigo, chips, extra) {
    return '<div class="vb-recibo" role="status">' +
      insignia +
      (codigo ? '<p class="vb-recibo__etiqueta">Tu código</p><p class="vb-recibo__codigo">' + esc(codigo) + '</p>' : '') +
      (extra || '') +
      '<div class="vb-recibo__chips">' + chips + '</div>' +
    '</div>';
  }

  function montoDelPago() {
    return resumenDePago(estado.montoHoy, estado.disponibles, vivas(estado.cartas), planDe(estado.plan), estado.saldo);
  }

  /** Mientras el navegador sale al checkout de Shopify. */
  function htmlRedirigiendo() {
    var creado = estado.creado || {};
    var falta = typeof creado.total_a_pagar === 'number' ? creado.total_a_pagar : montoDelPago().falta;
    return htmlEncabezado('Te llevamos a pagar', null, null) +
      '<div class="vault-bounty__cuerpo">' +
        aviso('neutral', 'cargando', 'Abriendo el pago de ' + esc(pesos(falta)) + ' en el checkout de The Vault…', ' role="status"') +
      '</div>';
  }

  /** ADR 256 §2.3 — el Saldo cubrió todo: pagado, sin pasar por Shopify. */
  function htmlPagado() {
    var creado = estado.creado || {};
    var url = urlSegura(creado.url_seguimiento);
    var copias = totalDeCopias(vivas(estado.cartasPedidas));
    return htmlEncabezado('Pagado con tu saldo', null, null) +
      '<div class="vault-bounty__cuerpo">' +
        htmlRecibo(chip('success', 'check', 'Bounty pagado'), creado.codigo,
          '<span class="rs__pill rs__pill--saldo">' + icono('monedas', 12) + ' −' + esc(pesos(creado.saldo_usado)) + ' de tu saldo</span>' +
          chip('neutral', 'reloj', 'Vence el ' + esc(fechaMx(creado.vence_at || venceAt(estado.plan)))) +
          (copias > 0 ? chip('pending', 'buscar', copias + (copias === 1 ? ' carta por conseguir' : ' cartas por conseguir')) : '')) +
        aviso('info', 'info', 'No se cobró nada a tu tarjeta. Lo que no se consiga, o salga más barato, regresa a tu saldo.') +
      '</div>' +
      '<footer class="vault-bounty__pie">' +
        (url
          ? '<a class="vb-cta vault-bounty__primario" href="' + esc(url) + '" data-bounty-foco-inicial>Ver mi pedido</a>'
          : '<button type="button" class="vb-cta vault-bounty__primario" data-bounty-cerrar data-bounty-foco-inicial>Listo</button>') +
      '</footer>';
  }

  /**
   * Solo con `?bounty_mock=`: en vez de salir al checkout, el recibo de lo que
   * se habría cobrado.
   */
  function htmlSimulado() {
    var creado = estado.creado || {};
    var resumen = montoDelPago();
    return htmlEncabezado('Pago simulado', null, chip('warning', 'alerta', 'Simulador')) +
      '<div class="vault-bounty__cuerpo">' +
        htmlRecibo(chip('success', 'check', 'Bounty listo para pagar'), creado.codigo,
          chip('neutral', 'monedas', esc(resumen.desglose)) +
          chip('neutral', 'reloj', 'Vence el ' + esc(fechaMx(creado.vence_at || venceAt(estado.plan)))),
          '<p class="vb-recibo__etiqueta">Aquí te mandaríamos a pagar</p>' +
          '<p class="vb-recibo__codigo">' + esc(pesos(resumen.pago.total)) + ' <small>(simulado)</small></p>') +
        aviso('info', 'info', 'Link de pago que devolvió el simulador: <code>' + esc(urlDePago(creado.invoice_url) || '') + '</code>') +
      '</div>' +
      '<footer class="vault-bounty__pie">' +
        '<button type="button" class="vb-cta vault-bounty__primario" data-bounty-cerrar data-bounty-foco-inicial>Listo</button>' +
      '</footer>';
  }

  /** 409: una disponible se vendió entre la búsqueda y el clic. No se cobró nada. */
  function htmlSinStock() {
    var nombres = estado.agotadas || [];
    var cual = nombres.length === 0
      ? 'Una de tus cartas disponibles se acaba de agotar.'
      : nombres.length === 1
        ? '<strong>' + esc(nombres[0]) + '</strong> se acaba de agotar.'
        : 'Se acaban de agotar: <strong>' + nombres.map(esc).join(', ') + '</strong>.';
    return htmlEncabezado('Una carta se agotó', null, null) +
      '<div class="vault-bounty__cuerpo">' +
        aviso('danger', 'error', cual + ' No se cobró nada. Vuelve a buscar tu lista para ver lo que queda y pagar todo junto.', ' role="alert"') +
      '</div>' +
      '<footer class="vault-bounty__pie">' +
        (estado.rebuscar
          ? '<button type="button" class="vb-cta vault-bounty__primario" data-bounty-rebuscar data-bounty-foco-inicial>Volver a buscar mi lista</button>'
          : '<button type="button" class="vb-cta vault-bounty__primario" data-bounty-cerrar data-bounty-foco-inicial>Volver a mi lista</button>') +
      '</footer>';
  }

  /* ------------------------------------------------------------------ *
   * 3c. La ficha de la carta (PDP)
   * ------------------------------------------------------------------ */

  /**
   * En la ficha de una carta que no tenemos (ningún single de esa carta a la
   * venta), el mismo banner del buscador: "Consíguela", y abre el modal con
   * esa carta. Liquid solo pinta el contenedor `[data-vault-bounty-ficha]`
   * cuando la carta está agotada; aquí se llena.
   *
   * Dawn reemplaza la zona de compra al cambiar de variante, así que el
   * contenedor puede llegar después: un observador lo monta cuando aparece, y
   * el clic se escucha en el documento.
   */
  function cartaDeLaFicha(nodo) {
    var nombre = (nodo.getAttribute('data-nombre') || '').trim();
    if (!nombre) return null;
    return {
      nombre: nombre,
      cantidad: 1,
      set: nodo.getAttribute('data-set') || null,
      numero: nodo.getAttribute('data-numero') || null,
      shopify_product_id: nodo.getAttribute('data-product-id') || null,
      imagen: nodo.getAttribute('data-imagen') || null
    };
  }

  function montarFichas() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-vault-bounty-ficha]:not([data-montada])'), function (nodo) {
      var carta = cartaDeLaFicha(nodo);
      if (!carta) return;
      nodo.setAttribute('data-montada', '');
      nodo.innerHTML = htmlSalidas({ cartas: [carta], disponibles: 0 });
      nodo.hidden = false;
    });
  }

  // Sin DOM (las pruebas del núcleo corren el archivo en node) no hay ficha.
  if (typeof document !== 'undefined' && document.addEventListener) {
    document.addEventListener('click', function (evento) {
      var boton = evento.target.closest && evento.target.closest('[data-vault-bounty-ficha] [data-bounty-abrir]');
      if (!boton) return;
      var carta = cartaDeLaFicha(boton.closest('[data-vault-bounty-ficha]'));
      if (carta) abrir({ origen: 'busqueda', cartas: [carta] });
    });

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', montarFichas);
    else montarFichas();
    if (window.MutationObserver && document.body) {
      new MutationObserver(function () {
        if (document.querySelector('[data-vault-bounty-ficha]:not([data-montada])')) montarFichas();
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  window.VaultBounty = {
    // Cuentas puras (probadas en docs/pruebas/vault-bounty.nucleo.mjs)
    PLANES: PLANES,
    MAX_CARTAS: MAX_CARTAS,
    planDe: planDe,
    rangoDeCarta: rangoDeCarta,
    rangoTotal: rangoTotal,
    venceAt: venceAt,
    fechaMx: fechaMx,
    pesos: pesos,
    cuerpoDeSolicitud: cuerpoDeSolicitud,
    cuerpoDeCheckout: cuerpoDeCheckout,
    totalAPagar: totalAPagar,
    resumenDePago: resumenDePago,
    saldoQueUsa: saldoQueUsa,
    urlDePago: urlDePago,
    nombresAgotados: nombresAgotados,
    unirCotizacion: unirCotizacion,
    urlSegura: urlSegura,
    mensajeDeError: mensajeDeError,
    nombreSinEdicion: nombreSinEdicion,
    varianteDeOtra: varianteDeOtra,
    // API
    apiBase: apiBase,
    cotizar: cotizar,
    pagar: pagar,
    // Interfaz
    htmlSalidas: htmlSalidas,
    htmlOtraVersion: htmlOtraVersion,
    abrir: abrir
  };
})();
